/**
 * 3D Planet — op-maat configurator (upload → analyse → 3D-preview → prijs → winkelwagen).
 *
 * Structuur van dit bestand:
 *   1. Constanten en helpers
 *   2. Parsen in een Blob-Web-Worker (met voortgang), fallback op de hoofdthread;
 *      de parsers zelf (STL/3MF/OBJ/PLY/GLB/AMF/STEP/ZIP) staan in quote-parsers.js
 *   3. Three.js-viewer
 *   4. <quote-configurator> custom element (UI, prijs, winkelwagen, offerte)
 *
 * Upload en toevoegen zijn transacties: een upload-generatie per bestand (oude staat wordt gewist
 * vóór validatie, alleen het recentste parse-resultaat telt, koopknop vergrendeld tijdens parsen,
 * annuleren beëindigt de worker) en één /cart/add.js-post met het bestand als File in de FormData.
 *
 * Geen build-stap, geen framework. De prijs-engine staat in quote-pricing.js; de levertijdbelofte
 * (printuren × drukte) in tdp-delivery-date.js (deliveryPromise).
 */

/**
 * Vendor-bibliotheken (three r170, OrbitControls, fflate 0.8.2, occt-import-js 0.0.23) zijn thema-assets
 * (assets/vendor-*.js); de sectie geeft de asset_url's door via data-attributen, zodat er
 * geen externe CDN nodig is. Gevuld in connectedCallback via configureVendorUrls().
 */
const vendorUrls = { three: '', orbit: '', fflate: '', occt: '', occtWasm: '', parsers: '', fallbackWorker: '' };

function configureVendorUrls(dataset) {
  const resolve = (value) => (value ? new URL(value, document.baseURI).href : '');
  vendorUrls.three = resolve(dataset.threeSrc);
  vendorUrls.orbit = resolve(dataset.orbitSrc);
  vendorUrls.fflate = resolve(dataset.fflateSrc);
  vendorUrls.occt = resolve(dataset.occtSrc);
  vendorUrls.occtWasm = resolve(dataset.occtWasmSrc);
  vendorUrls.parsers = resolve(dataset.parsersSrc);
  vendorUrls.fallbackWorker = resolve(dataset.fallbackWorkerSrc);
}

/** Laadt een klassiek (UMD) script één keer; voor de fflate-fallback op de hoofdthread. */
const scriptPromises = new Map();
function loadClassicScript(url) {
  if (!scriptPromises.has(url)) {
    scriptPromises.set(
      url,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Script laden mislukt: ${url}`));
        document.head.append(script);
      }),
    );
  }
  return scriptPromises.get(url);
}

const QUANTITY_PRESETS = [1, 5, 10, 25, 50, 100];
/** Levertijdbanden zonder capaciteitsmodule (fallback): standaard 1-3 werkdagen, spoed 1 werkdag. */
const DELIVERY_STANDARD_DAYS = { minDays: 1, maxDays: 3 };
const DELIVERY_RUSH_DAYS = { minDays: 1, maxDays: 1 };
const DEFAULT_INSERT_COUNT = 2;
const MAX_INSERT_COUNT = 100;
/** Property "Printuren" op maatwerkregels in de winkelwagen ("3,2 u"), gebruikt voor de orderdrempel. */
const PRINT_HOURS_PROPERTY = 'Printuren';
/** Verborgen property met de spoedtoeslag in centen, zodat het spoedminimum per bestelling één keer telt. */
const RUSH_CENTS_PROPERTY = '_quote_rush_cents';

/** Maximale looptijd van de worker per bestand; daarna beëindigen en naar de offerte-route. */
const WORKER_TIMEOUT_MS = 90000;
const UNIT_HINT_MAX_MM = 5;
const UNIT_SCALE_OPTIONS = [
  { scale: 25.4, key: 'unit_inch' },
  { scale: 10, key: 'unit_cm' },
  { scale: 1000, key: 'unit_m' },
];
const TINY_VOLUME_CM3 = 0.1;
const HUGE_VOLUME_CM3 = 2000;
const THIN_WALL_MM = 1.5;
const BYTES_PER_MB = 1024 * 1024;

const ACCEPTED_EXTENSIONS = ['stl', '3mf', 'obj', 'step', 'stp', 'ply', 'glb', 'gltf', 'amf', 'zip'];
const ZIP_EXTENSIONS = ['3mf', 'amf', 'zip'];
/** Events waarna de winkelwagen-inhoud (en dus de orderkosten) opnieuw gecontroleerd wordt. */
const CART_CHANGE_EVENTS = ['cart:lines-update', 'cart:update', 'cart:error'];
const SAMPLE_FROM_QUANTITY = 10;
const CART_CHECK_DELAY_MS = 600;

const MATERIAL_TAGLINE_MAX = 45;

/** Korte tagline: eerste zin (of deel vóór een dubbele punt), afgekapt op een woordgrens ≤ `max` tekens. */
function shortTagline(text, max) {
  const sentence = String(text || '')
    .trim()
    .split(/[.:]\s+/)[0]
    .replace(/[.:]$/, '')
    .trim();
  if (sentence.length <= max) return sentence;
  const cut = sentence.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[,;\s]+$/, '')}…`;
}

/** "PA-CF (nylon met koolstofvezel)" → { label: "PA-CF", sublabel: "nylon met koolstofvezel" }. */
function splitMaterialName(name) {
  const match = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(String(name || '').trim());
  return match ? { label: match[1].trim(), sublabel: match[2].trim() } : { label: String(name || '').trim(), sublabel: '' };
}

function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[char]);
}

function formatNumber(value, digits = 1, locale = 'nl-NL') {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}

function readJson(root, selector, fallback) {
  const node = root.querySelector(selector);
  if (!node) return fallback;
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    console.error('[quote-configurator] Ongeldige JSON in', selector, error);
    return fallback;
  }
}

function fileExtension(name) {
  const match = /\.([a-z0-9]+)$/i.exec(name || '');
  return match ? match[1].toLowerCase() : '';
}

/** Klantvriendelijke naam van het bestandsformaat voor meldingen. */
function formatLabel(extension) {
  return { stl: 'STL', '3mf': '3MF', obj: 'OBJ', step: 'STEP', stp: 'STEP', ply: 'PLY', glb: 'GLB', gltf: 'glTF', amf: 'AMF', zip: 'ZIP' }[extension] || extension.toUpperCase();
}

/* ------------------------------------------------------------------ */
/* 2. Parsen: Blob-Web-Worker (met voortgang) en fallback op de       */
/*    hoofdthread. De parsers zelf staan in assets/quote-parsers.js.  */
/* ------------------------------------------------------------------ */

let parsersModulePromise = null;
function loadParsersModule() {
  if (!parsersModulePromise) {
    if (!vendorUrls.parsers) return Promise.reject(new Error('Parser-module (quote-parsers.js) niet geconfigureerd'));
    parsersModulePromise = import(vendorUrls.parsers);
  }
  return parsersModulePromise;
}

let workerBlobUrl = null;
async function getWorkerUrl() {
  if (!workerBlobUrl) {
    const parsers = await loadParsersModule();
    workerBlobUrl = URL.createObjectURL(new Blob([parsers.buildWorkerSource()], { type: 'text/javascript' }));
  }
  return workerBlobUrl;
}

/** Fout wanneer een parse bewust is afgebroken (nieuwe upload, annuleren); geen melding voor de klant. */
class ParseCancelledError extends Error {}

function isWorkerInfrastructureError(error) {
  return error instanceof Error && /Worker|Blob|importScripts|Script laden|geconfigureerd/i.test(error.message);
}

/**
 * Parse-taak in een Blob-Web-Worker met annulering. `cancel()` beëindigt de worker direct
 * (geheugen vrij, geen late berichten); de promise verwerpt dan met ParseCancelledError.
 * Geeft { format, stats, objects, positions, warnings }; roept onProgress({ stage, percent }) aan.
 * @returns {{ promise: Promise<object>, cancel: () => void }}
 */
function startWorkerParse(buffer, extension, onProgress, { fallback = false, timeoutMs = fallback ? 20000 : WORKER_TIMEOUT_MS } = {}) {
  let worker = null;
  let cancelled = false;
  let finished = false;
  let settle = { resolve: () => {}, reject: () => {} };
  const promise = new Promise((resolve, reject) => {
    settle = { resolve, reject };
  });
  const finish = (callback, value) => {
    if (finished) return;
    finished = true;
    if (worker) worker.terminate();
    worker = null;
    callback(value);
  };
  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    finish(settle.reject, new ParseCancelledError('Verwerking afgebroken'));
  };
  let timer = 0;
  const timedOut = () => {
    const error = new Error(`Verwerking gestopt na ${timeoutMs / 1000} s`);
    error.code = 'timeout';
    error.seconds = timeoutMs / 1000;
    finish(settle.reject, error);
  };
  promise.finally(() => clearTimeout(timer)).catch(() => {});
  timer = setTimeout(timedOut, timeoutMs);
  (async () => {
    try {
      const [workerUrl, parsersModule] = await Promise.all([fallback ? Promise.resolve(vendorUrls.fallbackWorker) : getWorkerUrl(), loadParsersModule()]);
      if (cancelled || finished) return;
      if (!workerUrl) throw new Error("Fallback Worker niet geconfigureerd");
      worker = new Worker(workerUrl);
      worker.onmessage = (event) => {
        const data = event.data || {};
        if (data.progress) {
          if (onProgress) onProgress(data.progress);
          return;
        }
        if (data.ok) {
          finish(settle.resolve, data);
          return;
        }
        const error = new Error(data.error || 'Onbekende fout bij het lezen van het bestand');
        if (data.code) error.code = data.code;
        finish(settle.reject, error);
      };
      worker.onerror = (event) => finish(settle.reject, new Error(event.message || 'Worker-fout bij het lezen van het bestand'));
      worker.postMessage(
        {
          buffer,
          parsersUrl: vendorUrls.parsers,
          extension,
          fflateUrl: vendorUrls.fflate,
          occtUrl: vendorUrls.occt,
          occtWasmUrl: vendorUrls.occtWasm,
          manifoldLimit: parsersModule.MANIFOLD_CHECK_MAX_TRIANGLES,
          limits: fallback ? { ...parsersModule.DEFAULT_LIMITS, maxTriangles: 2000000, maxUnzippedBytes: 60 * BYTES_PER_MB } : parsersModule.DEFAULT_LIMITS,
        },
        [buffer],
      );
    } catch (error) {
      if (!cancelled) finish(settle.reject, error);
    }
  })();
  return { promise, cancel };
}

/** A synchronous main-thread parser cannot be forcibly interrupted. The legacy fallback
 * entry now uses a separately served worker; if workers are unavailable, ask for a quote. */
function parseOnMainThread(buffer, extension, onProgress) {
  return startWorkerParse(buffer, extension, onProgress, { fallback: true });
}

export function fallbackAllowed(buffer, extension, fileSize = buffer.byteLength) {
  if (fileSize > 60 * BYTES_PER_MB || buffer.byteLength > 60 * BYTES_PER_MB) return false;
  if (extension === 'stl' && buffer.byteLength >= 84) {
    const count = new DataView(buffer).getUint32(80, true);
    if (84 + count * 50 === buffer.byteLength && count > 2000000) return false;
  }
  return true;
}

/** Annulment owns both worker attempts; no CPU-intensive code runs on the UI thread. */
function startModelParse(file, onProgress = () => {}, deps = {}) {
  const extension = fileExtension(file.name);
  const read = deps.read || readFileWithProgress;
  const startWorker = deps.startWorker || startWorkerParse;
  const startFallback = deps.startFallback || parseOnMainThread;
  let cancelled = false;
  let workerJob = null;
  let cancelReject;
  const cancelledPromise = new Promise((_, reject) => { cancelReject = reject; });
  const work = (async () => {
    const buffer = await read(file, (percent) => onProgress({ stage: 'read', percent }));
    if (cancelled) throw new ParseCancelledError('Verwerking afgebroken');
    workerJob = startWorker(buffer.slice(0), extension, onProgress);
    try {
      return await workerJob.promise;
    } catch (workerError) {
      if (cancelled || workerError instanceof ParseCancelledError || !isWorkerInfrastructureError(workerError)) throw workerError;
      if (!fallbackAllowed(buffer, extension, file.size)) {
        const error = new Error('Dit bestand is te groot voor de reserveverwerking (60 MB / 2 miljoen driehoeken). Vraag een offerte aan; wij controleren het bestand handmatig.');
        error.code = 'limit';
        throw error;
      }
      workerJob = startFallback(buffer, extension, onProgress);
      return workerJob.promise;
    }
  })();
  return {
    promise: Promise.race([work, cancelledPromise]),
    cancel() {
      cancelled = true;
      workerJob?.cancel();
      cancelReject(new ParseCancelledError('Verwerking afgebroken'));
    },
  };
}

function readFileWithProgress(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    reader.onerror = () => reject(reader.error || new Error('Bestand kon niet worden gelezen'));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(file);
  });
}

/** "#rrggbb" → [r, g, b] in 0..1; ongeldig → lichtgrijs. */
function hexToRgb(hex) {
  const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return [0.77, 0.79, 0.82];
  const value = parseInt(match[1], 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

/**
 * Vertexkleuren (RGB per hoekpunt, 9 floats per driehoek) uit de paletindex per driehoek; driehoeken
 * zonder kleurinformatie krijgen de gekozen swatchkleur. Zonder kleurindex → null (effen kleur).
 * @param {Int16Array|null} colorIndex
 * @param {string[]} palette
 * @param {string} fallbackHex
 */
/** Palet na kleurtoewijzing: index → gekozen hex (colorMap) of de originele bestandskleur. */
function applyColorMap(palette, colorMap) {
  return (palette || []).map((hex, index) => (colorMap && colorMap[index]) || hex);
}

/** Aantal verschillende kleuren ná toewijzing (bepaalt of AMS nog nodig is). */
function effectiveColorCount(palette, colorMap) {
  return new Set(applyColorMap(palette, colorMap).map((hex) => String(hex).toLowerCase())).size;
}

function buildVertexColors(colorIndex, palette, fallbackHex) {
  if (!colorIndex || !palette || palette.length === 0) return null;
  const rgb = palette.map((hex) => hexToRgb(hex));
  const fallback = hexToRgb(fallbackHex);
  const colors = new Float32Array(colorIndex.length * 9);
  for (let i = 0; i < colorIndex.length; i += 1) {
    const [r, g, b] = colorIndex[i] >= 0 && colorIndex[i] < rgb.length ? rgb[colorIndex[i]] : fallback;
    for (let k = 0; k < 3; k += 1) {
      colors[i * 9 + k * 3] = r;
      colors[i * 9 + k * 3 + 1] = g;
      colors[i * 9 + k * 3 + 2] = b;
    }
  }
  return colors;
}

/* ------------------------------------------------------------------ */
/* 2a. Pure helpers (tests/quote-configurator.test.mjs)                */
/* ------------------------------------------------------------------ */

/** "1,2 mm" / "1.5" / 2 → millimeters als getal; onbruikbaar → 0. */
function parseMillimeters(value) {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : 0;
  const match = /-?\d+(?:[.,]\d+)?/.exec(String(value ?? ''));
  const parsed = match ? Number(match[0].replace(',', '.')) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * Materiaal en kleur uit de URL (`?material=<handle>&color=<naam>`, bijv. de knop "Print in dit
 * materiaal" op een materiaalpagina), gelezen vóór de eerste render. Onbekend → veilige default
 * (eerste materiaal, eerste beschikbare kleur); een kleur die niet bij het materiaal hoort of niet
 * op voorraad is wordt genegeerd.
 * @param {Array<{ handle: string, colors?: Array<{ name: string, inStock: boolean }> }>} materials
 * @param {string} search - window.location.search
 */
function initialSelection(materials, search) {
  const list = Array.isArray(materials) ? materials : [];
  let params;
  try {
    params = new URLSearchParams(String(search || ''));
  } catch (error) {
    params = new URLSearchParams();
  }
  const wantedMaterial = String(params.get('material') || '').trim().toLowerCase();
  const material = list.find((entry) => String(entry.handle).toLowerCase() === wantedMaterial) || list[0] || null;
  const colors = (material?.colors || []).filter((color) => color.inStock);
  const wantedColor = String(params.get('color') || '').trim().toLowerCase();
  const color = colors.find((entry) => entry.name.toLowerCase() === wantedColor) || colors[0] || null;
  return { materialHandle: material ? material.handle : null, colorName: color ? color.name : null };
}

const SWATCH_HEX = /^#[0-9a-f]{3,8}$/i;

/**
 * Eén kleur uit metaobject material.colors → interne vorm. Optioneel: finish ("Basic", "Mat", "Silk",
 * "Silk duo", "Silk trio"), hex2/hex3 (duo/trio) en transparent. Ongeldige hex-waarden vallen weg/terug.
 */
function normalizeMaterialColor(color) {
  const hex = (value) => (SWATCH_HEX.test(String(value || '').trim()) ? String(value).trim() : '');
  const normalized = {
    name: String(color?.name || ''),
    hex: hex(color?.hex) || '#888888',
    inStock: color?.in_stock !== false && color?.inStock !== false,
    finish: String(color?.finish || '').trim(),
    transparent: color?.transparent === true,
  };
  const hex2 = hex(color?.hex2);
  const hex3 = hex2 ? hex(color?.hex3) : '';
  return { ...normalized, ...(hex2 ? { hex2 } : {}), ...(hex3 ? { hex3 } : {}) };
}

/* ------------------------------------------------------------------ */
/* 2b. Winkelwagen-payload (pure functies; tests/quote-configurator.test.mjs) */
/* ------------------------------------------------------------------ */

/**
 * Bouwt de `items[]` voor één /cart/add.js-call:
 *   - orderkostenregel (product `orderkosten-maatwerk`) als de winkelwagen die nog niet heeft;
 *     assets/cart-order-fee.js bewaakt daarna de invariant (precies één zolang er maatwerk staat);
 *   - add-ons (Expertcheck) als aparte regels met `_addon_for` = `_quote_id`;
 *   - de maatwerkregel zelf (met het bestand als property "Bestand upload").
 * Volgorde: Shopify zet de laatst toegevoegde regel bovenaan → orderkosten eerst, hoofdregel laatst.
 * @param {{ main: { id: number|string, quantity: number, properties: object }, addons?: Array<{ variantId: number|string, label?: string }>,
 *           feeVariantId?: number|string, orderFeeInCart?: boolean, addonsModule?: object|null, cartModule: object }} input
 */
function buildCartItems({ main, addons = [], feeVariantId = 0, orderFeeInCart = false, addonsModule = null, cartModule }) {
  if (!main || !main.id) throw new RangeError('main.id (variant-id) is verplicht');
  const items = addonsModule
    ? addonsModule.cartDisplayOrder(addonsModule.buildCartPayload(main, addons, { key: main.properties?._quote_id }).items)
    : [{ id: Number(main.id), quantity: Math.max(1, Math.floor(Number(main.quantity) || 1)), properties: { ...(main.properties || {}) } }];
  const feeId = Number(feeVariantId);
  if (!orderFeeInCart && feeId) {
    return [{ id: feeId, quantity: cartModule.ORDER_FEE_QUANTITY, properties: cartModule.orderFeeLineProperties() }, ...items];
  }
  return items;
}

/** Schrijft `items[]` als multipart-velden; een File-waarde (bestand-upload) blijft een File. */
function itemsToFormData(items, formData = new FormData()) {
  items.forEach((item, index) => {
    formData.append(`items[${index}][id]`, String(item.id));
    formData.append(`items[${index}][quantity]`, String(item.quantity));
    for (const [name, value] of Object.entries(item.properties || {})) {
      if (value === undefined || value === null || value === '') continue;
      formData.append(`items[${index}][properties][${name}]`, value);
    }
  });
  return formData;
}

/** Staat de regel met deze `_quote_id` al in de winkelwagen? (na een onzekere /cart/add.js-response) */
/**
 * Som van de maatwerkregels die al in de winkelwagen staan: regeltotalen (centen, `_quote_total_cents`)
 * printuren (property "Printuren", bijv. "3,2 u") en spoedtoeslag (`_quote_rush_cents`). Voor de
 * offertedrempel, de gratis-Expertcheck-grens en het spoedminimum per bestelling.
 * @param {Array<{ properties?: object, quantity?: number }>} cartItems
 * @param {{ isQuoteLineItem: (item: object) => boolean }} cartModule
 * @returns {{ totalCents: number, printHours: number, rushSurchargeCents: number, lineCount: number }}
 */
/** Telefoon of tablet (ook iPadOS dat zich als Mac voordoet). */
function isTouchDevice(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return false;
  return /iPhone|iPad|iPod|Android/i.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1);
}

function orderFeePriceMatches(variantPrice, quote, config) {
  const expected = Math.round(config.orderFee * (config.parametersIncludeVat ? 1 : 1 + config.vatRate) * 100);
  return variantPrice !== undefined && variantPrice !== '' && Number(variantPrice) === expected;
}

function cartQuoteTotals(cartItems, cartModule) {
  const lines = (Array.isArray(cartItems) ? cartItems : []).filter((item) => cartModule.isQuoteLineItem(item));
  const totalCents = lines.reduce((sum, item) => sum + (Math.floor(Number(item.properties?._quote_total_cents)) || Math.floor(Number(item.quantity)) || 0), 0);
  const printHours = lines.reduce((sum, item) => sum + parsePrintHours(item.properties?.[PRINT_HOURS_PROPERTY]), 0);
  const rushSurchargeCents = lines.reduce((sum, item) => sum + Math.max(0, Math.floor(Number(item.properties?.[RUSH_CENTS_PROPERTY])) || 0), 0);
  return { totalCents, printHours: Math.round(printHours * 10) / 10, rushSurchargeCents, lineCount: lines.length };
}

/** "3,2 u" → 3.2; ontbrekend of ongeldig → 0. */
function parsePrintHours(value) {
  const number = Number(String(value ?? '').replace(/[^\d,.-]/g, '').replace(',', '.'));
  return Number.isFinite(number) && number > 0 ? number : 0;
}

function cartContainsQuote(cartItems, quoteId) {
  const wanted = String(quoteId || '');
  if (!wanted) return false;
  return (cartItems || []).some((item) => String(item?.properties?._quote_id || '') === wanted);
}

/* ------------------------------------------------------------------ */
/* 3. Three.js-viewer                                                  */
/* ------------------------------------------------------------------ */

let viewerPreloaded = false;
/** Injecteert <link rel="modulepreload"> voor de three-bundel zodra de bezoeker de dropzone benadert. */
function preloadViewerAssets() {
  if (viewerPreloaded || !vendorUrls.three || typeof document === 'undefined') return;
  viewerPreloaded = true;
  const link = document.createElement('link');
  link.rel = 'modulepreload';
  link.href = vendorUrls.three;
  document.head.appendChild(link);
}

let threePromise = null;
async function loadThree() {
  if (!threePromise) {
    threePromise = (async () => {
      if (!vendorUrls.three || !vendorUrls.orbit) throw new Error('Vendor-assets (three/OrbitControls) niet geconfigureerd');
      const THREE = await import(vendorUrls.three);
      // OrbitControls importeert het kale specifier 'three'; zonder importmap
      // patchen we dat naar de absolute asset-URL (zelfde module-instantie als hierboven).
      const source = await (await fetch(vendorUrls.orbit)).text();
      // Shopify's CDN minificeert JS-assets (from"three" zonder spatie), dus \s* i.p.v. \s+.
      const patched = source.replace(/from\s*['"]three['"]/g, `from '${vendorUrls.three}'`);
      const blobUrl = URL.createObjectURL(new Blob([patched], { type: 'text/javascript' }));
      const { OrbitControls } = await import(blobUrl);
      return { THREE, OrbitControls };
    })();
  }
  return threePromise;
}

class ModelViewer {
  constructor({ canvas, labelsRoot }) {
    this.canvas = canvas;
    this.labelsRoot = labelsRoot;
    this.mesh = null;
    this.box = null;
    this.grid = null;
    this.labels = [];
    this.running = false;
  }

  async init() {
    const { THREE, OrbitControls } = await loadThree();
    this.THREE = THREE;
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0b0d10);
    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 10000);
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x2a2f36, 1.1));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(1, 2, 1.5);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xdfe3e7, 0.5);
    fill.position.set(-2, 0.5, -1);
    this.scene.add(fill);
    // PLA-achtige matte look (lichte specular); kleur volgt de gekozen swatch (setColor) of de bestandskleuren (setVertexColors).
    this.solidColor = '#c4cad0';
    this.material = new THREE.MeshStandardMaterial({ color: this.solidColor, metalness: 0.05, roughness: 0.6 });
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas.parentElement);
    this.resize();
    this.start();
  }

  resize() {
    const parent = this.canvas.parentElement;
    const width = Math.max(parent.clientWidth, 1);
    const height = Math.max(parent.clientHeight, 1);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  setModel(positions, scale, vertexColors = null) {
    const { THREE } = this;
    this.clearModel();
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    this.applyVertexColors(geometry, vertexColors);
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox;
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    // Model is Z-omhoog (STL-conventie); Three.js is Y-omhoog.
    this.group = new THREE.Group();
    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.position.set(-center.x, -center.y, -bbox.min.z);
    this.group.add(this.mesh);
    this.group.rotation.x = -Math.PI / 2;
    this.group.scale.setScalar(scale);
    this.scene.add(this.group);

    const scaledSize = size.clone().multiplyScalar(scale);
    const maxDim = Math.max(scaledSize.x, scaledSize.y, scaledSize.z, 1);
    this.box = new THREE.Box3Helper(
      new THREE.Box3(
        new THREE.Vector3(-scaledSize.x / 2, 0, -scaledSize.y / 2),
        new THREE.Vector3(scaledSize.x / 2, scaledSize.z, scaledSize.y / 2),
      ),
      0xaeb5bc,
    );
    this.scene.add(this.box);
    const gridSize = Math.ceil((maxDim * 1.6) / 10) * 10;
    this.grid = new THREE.GridHelper(gridSize, Math.min(gridSize / 10, 40), 0x3a4148, 0x22272d);
    this.scene.add(this.grid);
    this.dimensions = { x: scaledSize.x, y: scaledSize.y, z: scaledSize.z };
    this.fitCamera(maxDim, scaledSize);
    this.buildLabels();
  }

  fitCamera(maxDim, size) {
    const distance = (maxDim / (2 * Math.tan((this.camera.fov * Math.PI) / 360))) * 1.7;
    this.camera.near = Math.max(distance / 100, 0.01);
    this.camera.far = distance * 20;
    this.camera.position.set(distance * 0.75, distance * 0.6, distance * 0.75);
    this.camera.updateProjectionMatrix();
    this.controls.target.set(0, size.z / 2, 0);
    this.controls.minDistance = maxDim * 0.3;
    this.controls.maxDistance = maxDim * 8;
    this.controls.update();
    this.homeState = { position: this.camera.position.clone(), target: this.controls.target.clone() };
  }

  resetView() {
    if (!this.homeState) return;
    this.camera.position.copy(this.homeState.position);
    this.controls.target.copy(this.homeState.target);
    this.controls.update();
  }

  buildLabels() {
    const { x, y, z } = this.dimensions;
    const { Vector3 } = this.THREE;
    const fmt = (v) => `${formatNumber(v, v >= 100 ? 0 : 1)} mm`;
    this.labelsRoot.replaceChildren();
    this.labels = [
      { text: `X ${fmt(x)}`, point: new Vector3(0, 0, y / 2) },
      { text: `Y ${fmt(y)}`, point: new Vector3(x / 2, 0, 0) },
      { text: `Z ${fmt(z)}`, point: new Vector3(-x / 2, z / 2, y / 2) },
    ].map((label) => {
      const el = document.createElement('span');
      el.className = 'qc__label';
      el.textContent = label.text;
      this.labelsRoot.append(el);
      return { ...label, el };
    });
  }

  updateLabels() {
    if (!this.labels.length) return;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    for (const label of this.labels) {
      const projected = label.point.clone().project(this.camera);
      const visible = projected.z < 1;
      label.el.style.transform = `translate(-50%, -50%) translate(${((projected.x + 1) / 2) * width}px, ${((1 - projected.y) / 2) * height}px)`;
      label.el.style.opacity = visible ? '1' : '0';
    }
  }

  /** Effen kleur (swatch); bij vertexkleuren is de basiskleur wit zodat de bestandskleuren niet getint worden. */
  setColor(hex) {
    this.solidColor = hex || '#c4cad0';
    if (!this.material.vertexColors) this.material.color.set(this.solidColor);
  }

  /** Bestandskleuren aan/uit zonder de geometrie opnieuw op te bouwen. */
  setVertexColors(vertexColors) {
    if (!this.mesh) return;
    this.applyVertexColors(this.mesh.geometry, vertexColors);
  }

  applyVertexColors(geometry, vertexColors) {
    const { THREE } = this;
    if (vertexColors) {
      geometry.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
      this.material.vertexColors = true;
      this.material.color.set('#ffffff');
    } else {
      if (geometry.hasAttribute('color')) geometry.deleteAttribute('color');
      this.material.vertexColors = false;
      this.material.color.set(this.solidColor);
    }
    this.material.needsUpdate = true;
  }

  clearModel() {
    for (const object of [this.group, this.box, this.grid]) {
      if (object) this.scene.remove(object);
    }
    if (this.mesh) this.mesh.geometry.dispose();
    this.mesh = null;
    this.group = null;
    this.box = null;
    this.grid = null;
    this.labelsRoot.replaceChildren();
    this.labels = [];
  }

  start() {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      if (!this.running) return;
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      this.updateLabels();
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
  }
}

/* ------------------------------------------------------------------ */
/* 4. Custom element                                                   */
/* ------------------------------------------------------------------ */

/** Fout uit /cart/add.js met een klantvriendelijke melding (al vertaald). */
class CartAddError extends Error {}

// In Node (tests) bestaat HTMLElement niet; de parsers blijven dan wel importeerbaar.
const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class QuoteConfigurator extends BaseElement {
  constructor() {
    super();
    this.state = {
      file: null,
      model: null, // { positions, stats, objects, format, warnings }
      scale: 1,
      materialHandle: null,
      colorName: null,
      qualityId: 'standard',
      infillId: 'normal',
      finishId: 'none',
      insertCount: DEFAULT_INSERT_COUNT,
      quantity: 1,
      rush: false,
      largeFormat: false,
      showIncl: true,
      orderFeeInCart: false,
      /** Maatwerk dat al in de winkelwagen staat: telt mee voor de offertedrempel en de printuren per bestelling. */
      cartQuoteTotalCents: 0,
      cartPrintHours: 0,
      /** Spoedtoeslag die al in de winkelwagen zit (centen): het spoedminimum geldt één keer per bestelling. */
      cartRushSurchargeCents: 0,
      /** true zolang een upload wordt gelezen/geparset: koopknop vergrendeld, oude staat al gewist. */
      parsing: false,
      checks: [],
      quote: null,
    };
    /** Upload-generatie: alleen het resultaat van de recentste upload wordt geaccepteerd. */
    this.uploadGeneration = 0;
    this.activeParse = null;
    this.addInFlight = false;
    /** true als de afwerking automatisch op "Meerkleurig (AMS)" is gezet (meerkleurig bestand). */
    this.finishAutoSelected = false;
  }

  async connectedCallback() {
    configureVendorUrls(this.dataset);
    this.settings = readJson(this, '[data-quote-config]', {});
    this.i18n = readJson(this, '[data-i18n]', {});
    const rawMaterials = readJson(this, '[data-quote-materials]', []);
    this.locale = this.settings.locale || 'nl-NL';
    this.currency = this.settings.currency || 'EUR';
    this.moneyFormatter = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });
    this.cacheElements();

    try {
      [this.pricing, this.cartModule, this.checks, this.colorTools] = await Promise.all([
        import(new URL(this.dataset.pricingSrc, document.baseURI).href),
        import(new URL(this.dataset.quoteCartSrc, document.baseURI).href),
        import(new URL(this.dataset.checksSrc, document.baseURI).href),
        import(new URL(this.dataset.colorsSrc, document.baseURI).href),
      ]);
    } catch (error) {
      console.error('[quote-configurator] Prijs-engine laden mislukt', error);
      this.showCtaError(this.t('error_cart'));
      return;
    }
    // Extra's (Expertcheck) zijn optioneel: zonder module blijft de stap "Extra's" leeg.
    this.addons = this.dataset.addonsSrc
      ? await import(new URL(this.dataset.addonsSrc, document.baseURI).href).catch((error) => {
          console.warn('[quote-configurator] Add-ons-module niet geladen', error);
          return null;
        })
      : null;
    // Bezorgdatum (werkdagen) is optioneel: zonder module blijven de labels zonder datum.
    // Preview-decimatie als geversioneerde asset (cache-busting via asset_url), net als de andere modules.
    this.previewModule = await import(this.dataset.previewSrc ? new URL(this.dataset.previewSrc, document.baseURI).href : './quote-preview.js');
    this.deliveryModule = this.dataset.deliverySrc
      ? await import(new URL(this.dataset.deliverySrc, document.baseURI).href).catch((error) => {
          console.warn('[quote-configurator] Bezorgdatum-module niet geladen', error);
          return null;
        })
      : null;
    this.materials = this.normalizeMaterials(rawMaterials);
    this.config = this.pricing.buildPricingConfig(this.settings, this.materials);
    // ?material=<handle>&color=<naam> (materiaalpagina's) vóór de eerste render; onbekend → default.
    const selection = initialSelection(this.config.materials, typeof window !== 'undefined' ? window.location.search : '');
    this.state.materialHandle = selection.materialHandle;
    this.state.colorName = selection.colorName;

    this.renderParams();
    this.bindEvents();
    this.bindCartEvents();
    this.checkCartForOrderFee();
    this.updatePrice();
    this.els.params.removeAttribute('aria-busy');
    this.els.resetView.textContent = this.t('preview_reset');
    this.els.breakdown.querySelector('summary').textContent = this.t('price_breakdown');
    if (this.querySelector('.qc__form-success, .qc__form-error')) {
      this.els.quoteForm.hidden = false;
    }
  }

  disconnectedCallback() {
    this.viewer?.stop();
    this.unbindCartEvents();
  }

  /* ---------- helpers ---------- */

  t(key, vars = {}) {
    const template = this.i18n[key] ?? key;
    return template.replace(/\{(\w+)\}/g, (_, name) => (name in vars ? String(vars[name]) : `{${name}}`));
  }

  money(amount) {
    return this.moneyFormatter.format(amount);
  }

  normalizeMaterials(list) {
    return list
      .filter((material) => material && material.handle && material.name)
      .map((material) => ({
        handle: String(material.handle),
        name: String(material.name),
        density: Number(material.density),
        pricePerGram: Number(material.pricePerGram),
        speedFactor: Number(material.speedFactor) || 1,
        minWall: parseMillimeters(material.minWall),
        description: material.description || '',
        // Metaobject-veld `tagline` heeft voorrang; anders de eerste zin van de omschrijving (≤ 45 tekens).
        tagline: String(material.tagline || '').trim() || shortTagline(material.description, MATERIAL_TAGLINE_MAX),
        url: material.url || '',
        sortOrder: Number(material.sortOrder) || 0,
        colors: Array.isArray(material.colors)
          ? material.colors.map(normalizeMaterialColor)
          : [],
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  materialByHandle(handle) {
    return this.config.materials.find((material) => material.handle === handle) || null;
  }

  firstAvailableColor(handle) {
    const material = this.materialByHandle(handle);
    return material?.colors?.find((color) => color.inStock)?.name || null;
  }

  cacheElements() {
    const q = (selector) => this.querySelector(selector);
    this.els = {
      dropzone: q('[data-qc-dropzone]'),
      fileInput: q('[data-qc-file-input]'),
      uploadButton: q('[data-qc-upload-button]'),
      progress: q('[data-qc-progress]'),
      viewer: q('[data-qc-viewer]'),
      canvas: q('[data-qc-canvas]'),
      labels: q('[data-qc-labels]'),
      resetView: q('[data-qc-reset-view]'),
      viewerNote: q('[data-qc-viewer-note]'),
      stats: q('[data-qc-stats]'),
      unitHint: q('[data-qc-unit-hint]'),
      messages: q('[data-qc-messages]'),
      params: q('[data-qc-params]'),
      total: q('[data-qc-total]'),
      unitPrice: q('[data-qc-unit-price]'),
      breakdown: q('[data-qc-breakdown]'),
      breakdownList: q('[data-qc-breakdown-list]'),
      addToCart: q('[data-qc-add-to-cart]'),
      addonsStep: q('[data-qc-addons-step]'),
      ctaError: q('[data-qc-cta-error]'),
      quoteForm: q('[data-qc-quote-form]'),
      quoteReason: q('[data-qc-quote-reason]'),
      cancelButton: q('[data-qc-cancel]'),
    };
  }

  /* ---------- parameters UI ---------- */

  renderParams() {
    const { config } = this;
    const radioGroup = (name, label, options, selected) => `
      <fieldset class="qc__group" data-qc-group="${name}">
        <legend class="qc__legend">${esc(label)}</legend>
        <div class="qc__options">${this.optionsHtml(name, options, selected)}</div>
      </fieldset>`;

    const materialOptions = config.materials.map((material) => ({
      value: material.handle,
      ...splitMaterialName(material.name),
      tagline: material.tagline,
      badge: this.optionBadge('material', material.handle),
      meta: this.t('material_from', { price: this.money(material.pricePerGram) }),
      detail: material.description,
      detailUrl: material.url,
    }));
    const qualityOptions = config.qualities.map((quality) => ({ value: quality.id, label: this.t(`quality_${quality.id}`), badge: this.optionBadge('quality', quality.id), tagline: this.i18n[`quality_${quality.id}_feel`] ? this.t(`quality_${quality.id}_feel`) : '' }));
    const infillOptions = config.infills.map((infill) => ({ value: infill.id, label: this.t(`infill_${infill.id}`) }));
    const finishOptions = config.finishes.map((finish) => ({
      value: finish.id,
      label: this.t(`finish_${finish.id}`),
      meta: this.finishMeta(finish),
    }));

    const stepHead = (n, title) => `
      <button type="button" class="qc__step-head" data-qc-step-head="${n}" aria-expanded="false" aria-controls="qc-step-${n}-${this.dataset.sectionId || 'x'}">
        <span class="qc__step-num">${n}</span>
        <span class="qc__step-title">${esc(title)}</span>
        <span class="qc__step-summary" data-qc-step-summary="${n}"></span>
        <span class="qc__step-action" data-qc-step-action="${n}"></span>
      </button>`;
    this.els.params.innerHTML = `
      <section class="qc__step" data-qc-step="2">
      ${stepHead(2, this.t('step_options'))}
      <div class="qc__step-body" id="qc-step-2-${this.dataset.sectionId || 'x'}" data-qc-step-body="2" hidden>
      ${radioGroup('material', this.t('param_material'), materialOptions, this.state.materialHandle)}
      <fieldset class="qc__group" data-qc-group="color">
        <legend class="qc__legend">${esc(this.t('param_color'))}</legend>
        <div class="qc__swatches" data-qc-swatches></div>
        <div class="qc__pmap" data-qc-palette-map hidden></div>
        <details class="qc__colorwish">
          <summary>${esc(this.t('color_wish_title'))}</summary>
          <label class="qc__quantity-custom"><span>${esc(this.t('color_wish_label'))}</span><input type="text" maxlength="40" data-qc-color-wish value="${esc(this.state.colorWish || '')}" placeholder="bv. RAL 5015 of #1e6fd6"></label>
          <p class="qc__muted qc__small">${esc(this.t('color_wish_note'))}</p>
        </details>
      </fieldset>
      ${radioGroup('quality', this.t('param_quality'), qualityOptions, this.state.qualityId)}
      ${radioGroup('infill', this.t('param_infill'), infillOptions, this.state.infillId)}
      ${radioGroup('finish', this.t('param_finish'), finishOptions, this.state.finishId)}
      <div class="qc__group qc__group--inline" data-qc-inserts ${this.state.finishId === 'inserts' ? '' : 'hidden'}>
        <label class="qc__quantity-custom">
          <span>${esc(this.t('param_inserts'))}</span>
          <input type="number" min="1" max="${MAX_INSERT_COUNT}" step="1" inputmode="numeric" value="${this.state.insertCount}" aria-label="${esc(this.t('aria_inserts_input'))}" data-qc-inserts-input>
        </label>
      </div>
      <button type="button" class="qc__button qc__button--primary qc__step-next" data-qc-step-next="3">${esc(this.t('step_next'))}</button>
      </div>
      </section>
      <section class="qc__step" data-qc-step="3">
      ${stepHead(3, this.t('step_order'))}
      <div class="qc__step-body" id="qc-step-3-${this.dataset.sectionId || 'x'}" data-qc-step-body="3" hidden>
      <fieldset class="qc__group" data-qc-group="quantity">
        <legend class="qc__legend">${esc(this.t('param_quantity'))}</legend>
        <div class="qc__quantities" data-qc-quantities></div>
        <label class="qc__quantity-custom">
          <span>${esc(this.t('param_quantity_custom'))}</span>
          <input type="number" min="1" max="10000" step="1" inputmode="numeric" value="${this.state.quantity}" aria-label="${esc(this.t('aria_quantity_input'))}" data-qc-quantity-input>
        </label>
        <label class="qc__sample" data-qc-sample-wrap hidden>
          <input type="checkbox" data-qc-sample>
          <span><strong>${esc(this.t('sample_label'))}</strong> <span class="qc__muted">${esc(this.t('sample_text'))}</span>
          <span class="qc__sample-note" data-qc-sample-note hidden>${esc(this.t('sample_note'))}</span></span>
        </label>
      </fieldset>
      <label class="qc__group">Bestemming
        <select data-qc-destination aria-label="Bestemming">
          ${[['NL', 'Nederland'], ['BE', 'België'], ['DE', 'Duitsland'], ['EU', 'Overig EU']].map(([value, name]) => `<option value="${value}" ${(this.state.destination || 'NL') === value ? 'selected' : ''}>${name}</option>`).join('')}
        </select>
      </label>
      ${radioGroup('delivery', this.t('param_delivery'), this.deliveryOptions(), this.state.rush ? 'rush' : 'standard')}
      <div class="qc__group qc__group--inline">
        <label class="qc__checkbox">
          <input type="checkbox" data-qc-large-format ${this.state.largeFormat ? 'checked' : ''}>
          <span>${esc(this.t('param_large_format', { max: this.settings.large_format_mm || 320, percent: config.largeFormatSurchargePercent }))}</span>
        </label>
        <div class="qc__vat" role="group" aria-label="${esc(this.t('param_vat'))}">
          <button type="button" class="qc__vat-button" data-qc-vat="incl" aria-pressed="${this.state.showIncl}">${esc(this.t('vat_incl'))}</button>
          <button type="button" class="qc__vat-button" data-qc-vat="excl" aria-pressed="${!this.state.showIncl}">${esc(this.t('vat_excl'))}</button>
        </div>
      </div>
      </div>
      </section>`;
    this.renderSwatches();
    this.renderQuantities();
    // Prijsuitsplitsing en extra's (Expertcheck) horen bij stap 3; de vaste prijsbalk blijft compact
    // (totaal + knop), vooral op mobiel. Bestaande nodes verplaatsen behoudt event-listeners.
    const body3 = this.els.params.querySelector('[data-qc-step-body="3"]');
    if (body3) {
      const cta = body3.querySelector('[data-qc-step-next]');
      // Alleen de uitsplitsing verhuist; de extra's (Expertcheck + akkoord-vinkje) blijven bij de knop,
      // zodat de validatie altijd zichtbaar is en de klant ze niet in een dichtgeklapte stap mist.
      for (const node of [this.els.breakdown]) {
        if (node && node.parentElement !== body3) body3.appendChild(node);
      }
      void cta;
    }
    this.updateSteps();
  }

  /* ---------- stappen (progressive disclosure) ---------- */

  /** Stap openen: 1 = upload (preview-kolom), 2 = materiaal/kleur/kwaliteit, 3 = aantal/levering. */
  openStep(n, { fromUser = false } = {}) {
    this.stepOpen = n;
    if (n > (this.stepUnlocked || 1)) this.stepUnlocked = n;
    this.updateSteps();
    // Het Expertcheck-aanbod alleen na een eigen klik op "Verder"/de stapkop, nooit bij programmatisch openen.
    if (n === 3 && fromUser) this.maybeShowUpsell();
    if (n > 1) {
      const body = this.els.params.querySelector(`[data-qc-step-body="${n}"]`);
      body?.closest('.qc__step')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** Koppen, samenvattingen en zichtbaarheid van de stappen bijwerken (idempotent). */
  updateSteps() {
    // Stap 2 (materiaal/kleur) staat vanaf het begin open zodat kleuren en materialen direct zichtbaar zijn;
    // stap 3 (aantal/levering) klapt uit na "Verder" of zodra er een model is en de klant verder wil.
    const open = this.stepOpen || 2;
    const unlocked = Math.max(this.stepUnlocked || 1, 2);
    for (const n of [2, 3]) {
      const head = this.els.params.querySelector(`[data-qc-step-head="${n}"]`);
      const body = this.els.params.querySelector(`[data-qc-step-body="${n}"]`);
      const action = this.els.params.querySelector(`[data-qc-step-action="${n}"]`);
      if (!head || !body) continue;
      const isOpen = open === n;
      body.hidden = !isOpen;
      head.setAttribute('aria-expanded', String(isOpen));
      head.classList.toggle('is-open', isOpen);
      head.classList.toggle('is-done', !isOpen && unlocked > n);
      head.classList.toggle('is-locked', unlocked < n);
      if (action) action.textContent = isOpen ? '' : unlocked >= n ? this.t('step_change') : this.t('step_locked');
    }
    this.updateStepSummaries();
  }

  updateStepSummaries() {
    const metrics = this.modelMetrics();
    const s1 = this.querySelector('[data-qc-step-summary="1"]');
    if (s1) s1.textContent = metrics && this.state.file ? this.t('step_done_upload', { name: this.state.file.name, size: metrics.size.map((v) => formatNumber(v, 0)).join(' × ') }) : '';
    const s2 = this.els.params.querySelector('[data-qc-step-summary="2"]');
    if (s2) {
      const material = this.config.materials.find((m) => m.handle === this.state.materialHandle);
      const parts = [material?.name, this.state.colorName, this.t(`quality_${this.state.qualityId}`), this.t(`infill_${this.state.infillId}`)].filter(Boolean);
      s2.textContent = (this.stepOpen || 2) === 2 ? '' : parts.join(' · ');
    }
  }

  /** "bezorgd tussen di 30 sep en do 2 okt" voor de levertijd-keuze; leeg zonder module. */
  deliveryEstimate({ rush = this.state.rush, quote = this.state.quote } = {}) {
    if (!this.deliveryModule?.deliveryChain) return 'Levertijd wordt bevestigd op offerte';
    const promise = this.deliveryPromise(quote);
    return this.deliveryModule.deliveryChain(new Date(), {
      printHours: promise.printHours, load: promise.load, hoursPerDay: promise.hoursPerDay,
      rush, destination: this.state.destination || 'NL',
      expertcheck: Boolean(this.addonsUi?.getState()?.expertcheck),
      closedDates: this.deliveryModule.parseClosedDates(this.dataset.closedDates),
    })?.label || 'Levertijd op offerte';
  }

  /** Meta-tekst bij een afwerking: prijs per stuk, of per insert met minimum. */
  finishMeta(finish) {
    if (finish.pricePerInsert > 0) {
      return this.t('finish_inserts_meta', { price: this.money(finish.pricePerInsert), minimum: this.money(finish.minimumPrice || 0) });
    }
    return finish.pricePerUnit > 0 ? `+ ${this.money(finish.pricePerUnit)}` : '';
  }

  /**
   * Levertijdbelofte voor de hele bestelling (deze regel + maatwerk in de winkelwagen) uit de
   * printuren en de drukte-instelling (assets/tdp-delivery-date.js deliveryPromise).
   * Zonder module: vaste 1-3 werkdagen, spoed altijd mogelijk.
   */
  deliveryPromise(quote = this.state.quote) {
    const printHours = (quote ? quote.printHours : 0) + this.state.cartPrintHours;
    if (!this.deliveryModule?.deliveryPromise) {
      return { band: 'quote', ...DELIVERY_STANDARD_DAYS, rushMinDays: 1, rushMaxDays: 1, rushAllowed: false, extraDays: 0, load: 'normal', needsQuote: true, hoursPerDay: 80, printHours };
    }
    return this.deliveryModule.deliveryPromise({ printHours, load: this.settings.capacity_load, hoursPerDay: Number(this.settings.capacity_hours_per_day) });
  }

  /** "1-3 werkdagen" (module) of een vaste tekst zonder module. */
  businessDaysLabel(minDays, maxDays) {
    if (this.deliveryModule?.formatBusinessDays) return this.deliveryModule.formatBusinessDays(minDays, maxDays);
    return `${minDays}-${maxDays} werkdagen`;
  }

  /** Opties voor de levertijd-keuze; spoed is uitgeschakeld boven de spoedgrens of bij "zeer druk". */
  deliveryOptions(quote = this.state.quote) {
    const { config } = this;
    const promise = this.deliveryPromise(quote);
    const rushPercent = Math.round((config.rushMultiplier - 1) * 100);
    const checkDays = this.addonsUi?.getState()?.expertcheck ? 1 : 0;
    const rushDispatch = checkDays ? 'Spoed: verzending na Expertcheck en akkoord'
      : promise.extraDays === 1 ? 'Spoed: overmorgen verzonden (werkdagen)' : 'Spoed: volgende werkdag na productiestart verzonden';
    const standardMeta = [this.deliveryEstimate({ rush: false, quote })];
    if (promise.extraDays > 0) standardMeta.push(this.t('delivery_busy_note', { days: this.businessDaysLabel(promise.extraDays, promise.extraDays) }));
    if (quote) standardMeta.push(this.t('delivery_print_hours', { hours: formatNumber(promise.printHours, 1) }));
    let rushMeta = this.deliveryEstimate({ rush: true, quote });
    if (!promise.rushAllowed) {
      rushMeta = promise.band === 'rush'
        ? this.t('delivery_rush_unavailable_busy')
        : this.t('delivery_rush_unavailable_hours', { max: formatNumber(12, 0), hours: formatNumber(promise.printHours, 1) });
    }
    return [
      { value: 'standard', label: promise.needsQuote ? 'Standaard: levertijd op offerte' : this.t('delivery_standard', { days: this.businessDaysLabel(promise.minDays + checkDays, promise.maxDays + checkDays) }), meta: standardMeta.filter(Boolean).join(' · ') },
      { value: 'rush', label: `${rushDispatch} · +${rushPercent}%, min. ${this.money(config.rushMinimum)}`, meta: rushMeta, disabled: !promise.rushAllowed },
    ];
  }

  rushShare() {
    return this.deliveryModule?.RUSH_SHARE ?? 0.15;
  }

  /** Vernieuwt alleen de levertijd-keuze (printuren veranderen met aantal/materiaal); zet spoed uit als die niet meer kan. */
  renderDelivery(quote = this.state.quote) {
    const group = this.els.params.querySelector('[data-qc-group="delivery"] .qc__options');
    if (!group) return;
    const options = this.deliveryOptions(quote);
    if (this.state.rush && options.some((option) => option.value === 'rush' && option.disabled)) this.state.rush = false;
    const selected = this.state.rush ? 'rush' : 'standard';
    group.innerHTML = this.optionsHtml('delivery', options, selected);
  }

  /** Badge ("Populair"/"Aanbevolen") per optie uit de sectie-instellingen (badge_popular_material enz.). */
  optionBadge(group, value) {
    const settings = this.settings || {};
    if (String(settings[`badge_popular_${group}`] || '') === String(value)) return this.t('badge_popular');
    if (String(settings[`badge_recommended_${group}`] || '') === String(value)) return this.t('badge_recommended');
    return '';
  }

  /* ---------- upsell-modal (Vistaprint: één aanbod op het juiste moment, met "Doorgaan zonder") ---------- */

  /** Eén keer per upload, bij het openen van stap 3: Expertcheck aanbieden als die nog niet gekozen is. */
  maybeShowUpsell() {
    if (!this.state.model || !this.addonsUi || !this.settings?.upsell_enabled) return;
    if (this.upsellShownFor === this.uploadGeneration) return;
    if (this.addonsUi.getState().expertcheck) return;
    this.upsellShownFor = this.uploadGeneration;
    const priceCents = this.addons.addonPriceCents ? this.addons.addonPriceCents('expertcheck', this.addonsUi.getState()) : 0;
    const price = priceCents === 0 ? this.t('upsell_free') : this.t('upsell_price', { price: this.money(priceCents / 100) });
    const modal = document.createElement('div');
    modal.className = 'qc__modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'qc-upsell-title');
    modal.dataset.qcUpsell = '';
    modal.innerHTML = `
      <div class="qc__modal-card">
        <p class="qc__modal-eyebrow">${esc(this.t('upsell_eyebrow'))}</p>
        <h3 class="qc__modal-title" id="qc-upsell-title">${esc(this.t('upsell_title'))}</h3>
        <p class="qc__modal-text">${esc(this.t('upsell_text'))}</p>
        <p class="qc__modal-price">${esc(price)}</p>
        <div class="qc__modal-actions">
          <button type="button" class="qc__button qc__button--primary" data-qc-upsell-add>${esc(this.t('upsell_add'))}</button>
          <button type="button" class="qc__button qc__button--ghost" data-qc-upsell-skip>${esc(this.t('upsell_skip'))}</button>
        </div>
      </div>`;
    const close = () => { modal.remove(); document.removeEventListener('keydown', onKey); };
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-qc-upsell-add]')) {
        this.addonsUi.update({ expertcheck: true, approved: false });
        this.updatePrice();
        close();
        return;
      }
      if (event.target.closest('[data-qc-upsell-skip]') || event.target === modal) close();
    });
    document.addEventListener('keydown', onKey);
    this.appendChild(modal);
    modal.querySelector('[data-qc-upsell-skip]')?.focus();
  }

  /* ---------- proefexemplaar (Vistaprint "eerst één proberen") ---------- */

  /** Toont de proefexemplaar-optie bij ≥ 10 stuks; aangevinkt = aantal 1 en property voor verrekening. */
  updateSampleOption() {
    const wrap = this.els.params.querySelector('[data-qc-sample-wrap]');
    if (!wrap) return;
    const threshold = Number(this.settings?.sample_from_quantity) || SAMPLE_FROM_QUANTITY;
    wrap.hidden = !(this.state.sample || this.state.quantity >= threshold);
    const box = wrap.querySelector('[data-qc-sample]');
    if (box) box.checked = Boolean(this.state.sample);
    const note = wrap.querySelector('[data-qc-sample-note]');
    if (note) note.hidden = !this.state.sample;
  }

  toggleSample(checked) {
    if (checked) {
      this.state.sampleForQuantity = this.state.quantity;
      this.state.sample = true;
      this.setQuantity(1);
    } else {
      this.state.sample = false;
      this.setQuantity(this.state.sampleForQuantity || 1);
    }
    this.updateSampleOption();
  }

  /** Radio-opties van één groep (materiaal, kwaliteit, levertijd, …). */
  optionsHtml(name, options, selected) {
    return options
      .map(
        (option) => `
            <div class="qc__option-wrap${option.detail ? ' qc__option-wrap--info' : ''}">
              <label class="qc__option${option.disabled ? ' qc__option--disabled' : ''}">
                <input type="radio" name="qc-${name}" value="${esc(option.value)}" ${option.value === selected ? 'checked' : ''} ${option.disabled ? 'disabled' : ''}>
                <span class="qc__option-body">
                  <span class="qc__option-title">${esc(option.label)}${option.sublabel ? `<span class="qc__option-sublabel">${esc(option.sublabel)}</span>` : ''}${option.badge ? `<span class="qc__option-badge">${esc(option.badge)}</span>` : ''}</span>
                  ${option.tagline ? `<span class="qc__option-tagline">${esc(option.tagline)}</span>` : ''}
                  ${option.meta ? `<span class="qc__option-meta">${esc(option.meta)}</span>` : ''}
                </span>
              </label>
              ${option.detail ? this.renderOptionDetail(name, option) : ''}
            </div>`,
      )
      .join('');
  }

  /** "i"-knop met uitklapbare volledige omschrijving (aria-expanded/aria-controls). */
  renderOptionDetail(groupName, option) {
    const id = `qc-${groupName}-detail-${String(option.value).replace(/[^a-z0-9_-]/gi, '')}`;
    const link = option.detailUrl
      ? `<a class="qc__option-detail-link" href="${esc(option.detailUrl)}" target="_blank" rel="noopener">${esc(this.t('material_more', { name: option.label }))}</a>`
      : '';
    return `
              <button type="button" class="qc__info" data-qc-info aria-expanded="false" aria-controls="${id}" aria-label="${esc(this.t('material_info', { name: option.label }))}">i</button>
              <div class="qc__option-detail" id="${id}" hidden>
                <p>${esc(option.detail)}</p>
                ${link}
              </div>`;
  }

  toggleOptionDetail(button) {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    const panel = this.els.params.querySelector(`#${button.getAttribute('aria-controls')}`);
    button.setAttribute('aria-expanded', String(!expanded));
    if (panel) panel.hidden = expanded;
  }

  renderSwatches() {
    const root = this.els.params.querySelector('[data-qc-swatches]');
    const material = this.materialByHandle(this.state.materialHandle);
    const colors = material?.colors || [];
    if (!colors.length) {
      root.innerHTML = `<p class="qc__muted">–</p>`;
      this.state.colorName = null;
      return;
    }
    const groups = this.colorGroups(colors);
    const renderSwatch = (color) => {
      const label = color.inStock ? color.name : this.t('color_out_of_stock', { name: color.name });
      const shortName = groups.length > 1 ? this.colorDisplayName(color) : color.name;
      return `
        <label class="qc__swatch${color.inStock ? '' : ' qc__swatch--out'}" title="${esc(label)}">
          <input type="radio" name="qc-color" value="${esc(color.name)}" ${color.name === this.state.colorName ? 'checked' : ''} ${color.inStock ? '' : 'disabled'} aria-label="${esc(this.t('aria_swatch', { name: label }))}">
          ${this.swatchDot(color, 'qc__swatch-dot')}
          <span class="qc__swatch-name">${esc(shortName)}</span>
        </label>`;
    };
    if (groups.length < 2) {
      root.innerHTML = colors.map(renderSwatch).join('');
    } else {
      // Per afwerking een inklapbare groep; open: de groep met de gekozen kleur (anders de eerste).
      const openIndex = Math.max(0, groups.findIndex((group) => group.colors.some((color) => color.name === this.state.colorName)));
      root.innerHTML = groups
        .map((group, index) => `
        <details class="qc__swatch-group" data-qc-finish="${esc(group.id)}"${index === openIndex ? ' open' : ''}>
          <summary class="qc__swatch-group-title">${esc(group.label)} <span class="qc__muted">(${group.colors.length})</span></summary>
          <div class="qc__swatch-group-list">${group.colors.map(renderSwatch).join('')}</div>
        </details>`)
        .join('');
    }
    this.renderPaletteMap();
  }

  /** Kleuren per afwerking (quote-colors.js); zonder module één groep. */
  colorGroups(colors) {
    return this.colorTools?.groupColorsByFinish ? this.colorTools.groupColorsByFinish(colors) : [{ id: 'all', label: '', colors }];
  }

  colorDisplayName(color) {
    return this.colorTools?.displayColorName ? this.colorTools.displayColorName(color) : color.name;
  }

  /** Staal: effen, duo/trio als verloop, transparant met dambordpatroon. */
  swatchDot(color, className) {
    const background = this.colorTools?.swatchBackground ? this.colorTools.swatchBackground(color) : color.hex;
    const classes = [className, color.transparent ? `${className}--clear` : '', color.finish && /^silk/i.test(color.finish) ? `${className}--silk` : ''].filter(Boolean).join(' ');
    return `<span class="${classes}" style="--swatch: ${esc(background)}"></span>`;
  }

  /** Effen filamentkleuren op voorraad; duo/trio kunnen geen losse bestandskleur vervangen. */
  mappableColors() {
    const colors = this.availableColors();
    return this.colorTools?.singleColors ? this.colorTools.singleColors(colors) : colors;
  }

  /**
   * Meerkleurig bestand: per herkende bestandskleur een rij "origineel → gekozen filamentkleur" (stalen van het
   * gekozen materiaal) met "Origineel". De keuze kleurt de 3D-preview direct en gaat als property mee.
   */
  renderPaletteMap() {
    const root = this.els.params.querySelector('[data-qc-palette-map]');
    if (!root) return;
    const palette = this.state.model?.palette || [];
    if (palette.length < 2) { root.hidden = true; root.innerHTML = ''; return; }
    const material = this.materialByHandle(this.state.materialHandle);
    const colors = this.mappableColors();
    const map = this.state.colorMap || {};
    const pmapChoice = (color, index) => `<label class="qc__pmap-choice" title="${esc(color.name)}"><input type="radio" name="qc-pmap-${index}" value="${esc(color.hex)}" data-color-name="${esc(color.name)}" ${map[index] && map[index].toLowerCase() === String(color.hex).toLowerCase() ? 'checked' : ''}>${this.swatchDot(color, 'qc__color-dot')}</label>`;
    const groups = this.colorGroups(colors);
    const pmapChoices = (index) => groups
      .map((group) => `${groups.length > 1 ? `<span class="qc__pmap-group">${esc(group.label)}</span>` : ''}${group.colors.map((color) => pmapChoice(color, index)).join('')}`)
      .join('');
    root.hidden = false;
    root.innerHTML = `
      <p class="qc__pmap-title">${esc(this.t('pmap_title', { count: palette.length }))}</p>
      <div class="qc__pmap-counter" data-qc-pmap-counter></div>
      ${palette.map((hex, index) => `
        <div class="qc__pmap-row" data-qc-pmap-row="${index}">
          <span class="qc__pmap-original"><span class="qc__color-dot" style="--swatch: ${esc(hex)}"></span>${esc(this.t('pmap_color_n', { n: index + 1 }))}</span>
          <span class="qc__pmap-arrow" aria-hidden="true">→</span>
          <span class="qc__pmap-choices" role="radiogroup" aria-label="${esc(this.t('pmap_color_n', { n: index + 1 }))}">
            <label class="qc__pmap-choice" title="${esc(this.t('pmap_original'))}"><input type="radio" name="qc-pmap-${index}" value="" ${map[index] ? '' : 'checked'}><span class="qc__color-dot qc__color-dot--outline" style="--swatch: ${esc(hex)}"></span><span class="qc__pmap-label">${esc(this.t('pmap_original'))}</span></label>
            ${pmapChoices(index)}
            ${this.paletteLikeChoice(palette, colors, map, index)}
          </span>
        </div>`).join('')}
      <p class="qc__muted qc__small">${esc(this.t('pmap_note'))}</p>`;
    this.renderPaletteCounter();
  }

  /** Na "samenvoegen" kan een kleur naar een andere bestandskleur wijzen (geen filament): toon die als "Zoals kleur m". */
  paletteLikeChoice(palette, colors, map, index) {
    const target = map[index] ? String(map[index]).toLowerCase() : '';
    if (!target || colors.some((color) => String(color.hex).toLowerCase() === target)) return '';
    const like = palette.findIndex((hex, other) => other !== index && String(hex).toLowerCase() === target);
    const label = like >= 0 ? this.t('pmap_like_n', { n: like + 1 }) : target;
    return `<label class="qc__pmap-choice" title="${esc(label)}"><input type="radio" name="qc-pmap-${index}" value="${esc(map[index])}" checked><span class="qc__color-dot" style="--swatch: ${esc(map[index])}"></span><span class="qc__pmap-label">${esc(label)}</span></label>`;
  }

  /** Teller "N van 9 kleuren" + knoppen "Vergelijkbare kleuren samenvoegen" / "Ongedaan maken". */
  renderPaletteCounter() {
    const root = this.els.params.querySelector('[data-qc-pmap-counter]');
    const palette = this.state.model?.palette || [];
    if (!root || palette.length < 2 || !this.colorTools) return;
    const max = this.colorTools.MAX_PRINT_COLORS;
    const count = effectiveColorCount(palette, this.state.colorMap);
    const mergeable = this.colorTools.mergeableCount(palette, this.state.colorMap, this.mappableColors());
    root.classList.toggle('qc__pmap-counter--over', count > max);
    root.innerHTML = `
      <span class="qc__pmap-count">${esc(this.t('pmap_counter', { count, max }))}</span>
      ${mergeable > 0 ? `<button type="button" class="qc__pmap-button" data-qc-pmap-merge>${esc(this.t('pmap_merge', { n: mergeable }))}</button>` : ''}
      ${this.state.colorMapUndo ? `<button type="button" class="qc__pmap-button qc__pmap-button--ghost" data-qc-pmap-undo>${esc(this.t('pmap_undo'))}</button>` : ''}`;
  }

  /** Filamentkleuren (op voorraad) van het gekozen materiaal. */
  availableColors() {
    const material = this.materialByHandle(this.state.materialHandle);
    return (material?.colors || []).filter((color) => color.inStock);
  }

  /** Knop "Vergelijkbare kleuren samenvoegen": ΔE00 < 3, nooit geforceerd tot 9; ongedaan maken blijft mogelijk. */
  mergeSimilarColors() {
    const palette = this.state.model?.palette || [];
    if (palette.length < 2 || !this.colorTools) return;
    const result = this.colorTools.mergeSimilarColors(palette, this.state.colorMap, this.mappableColors());
    if (result.merged <= 0) return;
    this.state.colorMapUndo = { ...(this.state.colorMap || {}) };
    this.state.colorMap = result.colorMap;
    this.renderPaletteMap();
    this.afterColorMapChange();
  }

  undoMergeColors() {
    if (!this.state.colorMapUndo) return;
    this.state.colorMap = this.state.colorMapUndo;
    this.state.colorMapUndo = null;
    this.renderPaletteMap();
    this.afterColorMapChange();
  }

  /** Toewijzing gewijzigd → afwerking (AMS of niet), preview, checks (9-kleurengrens), teller en prijs samen bijwerken. */
  afterColorMapChange() {
    const palette = this.state.model?.palette || [];
    // Alle kleuren gelijk → geen AMS nodig (afwerking Geen); anders Meerkleurig (AMS).
    if (palette.length >= 2) this.selectFinish(effectiveColorCount(palette, this.state.colorMap) === 1 ? 'none' : 'multicolor');
    this.toggleInsertsField();
    this.updatePreviewColors();
    this.runChecks();
    this.renderPaletteCounter();
    this.updatePrice();
  }

  /** Leesbare kleurtoewijzing voor de orderregel/offerte: "Kleur 1 (#ff0000) → Blauw; Kleur 2 → origineel". */
  colorMapProperty() {
    const palette = this.state.model?.palette || [];
    if (palette.length < 2) return '';
    const map = this.state.colorMap || {};
    const material = this.materialByHandle(this.state.materialHandle);
    return palette.map((hex, index) => {
      const target = map[index];
      const name = target ? (material?.colors?.find((c) => String(c.hex).toLowerCase() === target.toLowerCase())?.name || target) : this.t('pmap_original').toLowerCase();
      return `${this.t('pmap_color_n', { n: index + 1 })} (${hex}) → ${name}`;
    }).join('; ');
  }

  renderQuantities() {
    const root = this.els.params.querySelector('[data-qc-quantities]');
    const rows = this.state.model
      ? this.pricing.unitPricesForQuantities(this.quoteInput(1), QUANTITY_PRESETS, this.config)
      : QUANTITY_PRESETS.map((quantity) => ({ quantity }));
    root.innerHTML = rows
      .map((row) => {
        // Vóór upload: het staffelpercentage als placeholder ("−10%"), zodat de staffel ook zonder bestand zichtbaar is.
        const price = row.unitPriceIncl !== undefined
          ? this.money(this.state.showIncl ? row.unitPriceIncl : row.unitPriceExcl)
          : this.tierPlaceholder(row.quantity);
        const active = row.quantity === this.state.quantity;
        return `
        <button type="button" class="qc__qty" data-qc-qty="${row.quantity}" aria-pressed="${active}">
          <span class="qc__qty-count">${row.quantity}</span>
          <span class="qc__qty-price">${esc(price)}</span>
        </button>`;
      })
      .join('');
  }

  /** Placeholder voor een staffelknop zonder prijs: "−10%" bij korting, anders de basisprijs-tekst. */
  tierPlaceholder(quantity) {
    const percent = this.pricing.tierDiscountPercent(quantity, this.config);
    return percent > 0 ? `−${percent}%` : this.t('tier_base');
  }

  /* ---------- events ---------- */

  bindEvents() {
    const { els } = this;
    // iOS/Android kennen .stl/.3mf/.obj niet als bestandstype en maken ze grijs als 'accept' ze noemt
    // (alleen ZIP blijft kiesbaar). Op touch-apparaten daarom geen filter; het type wordt na het kiezen gecontroleerd.
    if (isTouchDevice()) els.fileInput.removeAttribute('accept');
    els.uploadButton.addEventListener('click', () => els.fileInput.click());
    els.dropzone.addEventListener('click', (event) => {
      // Knoppen in de dropzone (kies bestand, annuleren) hebben hun eigen handler.
      if (event.target instanceof Element && event.target.closest('button')) return;
      els.fileInput.click();
    });
    els.dropzone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        els.fileInput.click();
      }
    });
    els.dropzone.setAttribute('aria-label', this.t('aria_dropzone'));
    els.fileInput.addEventListener('change', () => {
      const file = els.fileInput.files?.[0];
      // Leegmaken zodat hetzelfde (bijv. gerepareerde) bestand opnieuw gekozen kan worden.
      els.fileInput.value = '';
      if (file) this.handleFile(file);
    });
    els.cancelButton?.addEventListener('click', () => this.cancelUpload());
    for (const eventName of ['dragenter', 'dragover']) {
      els.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        els.dropzone.classList.add('is-dragover');
      });
    }
    // Viewer-bundel pas voorladen bij de eerste intentie tot uploaden (niet bij page load).
    for (const eventName of ['dragenter', 'focusin', 'pointerenter']) {
      els.dropzone.addEventListener(eventName, () => preloadViewerAssets(), { once: true, passive: true });
    }
    for (const eventName of ['dragleave', 'drop']) {
      els.dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        els.dropzone.classList.remove('is-dragover');
      });
    }
    els.dropzone.addEventListener('drop', (event) => {
      const file = event.dataTransfer?.files?.[0];
      if (file) this.handleFile(file);
    });
    els.canvas.setAttribute('aria-label', this.t('aria_preview'));
    els.canvas.setAttribute('role', 'img');
    els.resetView.addEventListener('click', () => this.viewer?.resetView());

    this.bindQuoteRequest();
    els.params.addEventListener('change', (event) => this.onParamChange(event));
    els.params.addEventListener('input', (event) => {
      const wish = event.target.closest('[data-qc-color-wish]');
      if (wish) this.state.colorWish = wish.value.trim().slice(0, 40);
    });
    els.params.addEventListener('click', (event) => {
      const next = event.target.closest('[data-qc-step-next]');
      if (next) { this.openStep(Number(next.dataset.qcStepNext), { fromUser: true }); return; }
      const head = event.target.closest('button[data-qc-step-head]');
      if (head) this.openStep(Number(head.dataset.qcStepHead), { fromUser: true });
    });
    els.params.addEventListener('click', (event) => {
      if (event.target.closest('[data-qc-pmap-merge]')) { this.mergeSimilarColors(); return; }
      if (event.target.closest('[data-qc-pmap-undo]')) this.undoMergeColors();
    });
    els.params.addEventListener('click', (event) => {
      const infoButton = event.target.closest('[data-qc-info]');
      if (infoButton) {
        this.toggleOptionDetail(infoButton);
        return;
      }
      const qtyButton = event.target.closest('[data-qc-qty]');
      if (qtyButton) {
        this.setQuantity(Number(qtyButton.dataset.qcQty));
        return;
      }
      const vatButton = event.target.closest('[data-qc-vat]');
      if (vatButton) {
        this.state.showIncl = vatButton.dataset.qcVat === 'incl';
        for (const button of els.params.querySelectorAll('[data-qc-vat]')) {
          button.setAttribute('aria-pressed', String((button.dataset.qcVat === 'incl') === this.state.showIncl));
        }
        this.updatePrice();
      }
    });
    els.params.addEventListener('input', (event) => {
      if (event.target.matches('[data-qc-quantity-input]')) {
        const value = Math.floor(Number(event.target.value));
        if (Number.isFinite(value) && value >= 1) this.setQuantity(value, { fromInput: true });
      } else if (event.target.matches('[data-qc-inserts-input]')) {
        const value = Math.floor(Number(event.target.value));
        if (Number.isFinite(value) && value >= 1) {
          this.state.insertCount = Math.min(MAX_INSERT_COUNT, value);
          this.updatePrice();
        }
      }
    });
    els.unitHint.addEventListener('click', (event) => {
      const button = event.target.closest('[data-qc-scale]');
      if (button) this.applyScale(Number(button.dataset.qcScale));
      const relative = event.target.closest('[data-qc-scale-rel]');
      if (relative) this.applyScale(this.state.scale * Number(relative.dataset.qcScaleRel));
    });
    // Grootste maat in mm invoeren: schaalt het model (en dus volume en prijs) evenredig.
    els.unitHint.addEventListener('change', (event) => {
      const input = event.target.closest('[data-qc-size-mm]');
      if (!input) return;
      const metrics = this.modelMetrics();
      const target = parseFloat(String(input.value).replace(',', '.'));
      if (!metrics || !Number.isFinite(target) || target <= 0 || metrics.maxDim <= 0) return;
      this.applyScale(this.state.scale * (target / metrics.maxDim));
    });
    els.addToCart.addEventListener('click', () => this.addToCart());
  }

  /**
   * Orderkosten (1× per bestelling) hangen af van wat er al in de winkelwagen zit.
   * Na elke cart-wijziging (drawer, cart-pagina, terugnavigatie) herberekenen we.
   */
  bindCartEvents() {
    this.cartListeners = CART_CHANGE_EVENTS.map((eventName) => {
      const handler = () => this.scheduleCartCheck();
      document.addEventListener(eventName, handler);
      return { eventName, handler };
    });
    this.onPageShow = () => this.scheduleCartCheck();
    window.addEventListener('pageshow', this.onPageShow);
    this.onVisibility = () => {
      if (document.visibilityState === 'visible') this.scheduleCartCheck();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  unbindCartEvents() {
    for (const { eventName, handler } of this.cartListeners || []) document.removeEventListener(eventName, handler);
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    this.cartListeners = [];
  }

  /** Cart-events komen vóór de server-response; korte vertraging zodat /cart.js actueel is. */
  scheduleCartCheck() {
    clearTimeout(this.cartCheckTimer);
    this.cartCheckTimer = setTimeout(() => this.checkCartForOrderFee(), CART_CHECK_DELAY_MS);
  }

  onParamChange(event) {
    queueMicrotask(() => this.updateStepSummaries());
    const pmap = event.target.name && /^qc-pmap-(\d+)$/.exec(event.target.name);
    if (pmap) {
      const index = Number(pmap[1]);
      const map = { ...(this.state.colorMap || {}) };
      if (event.target.value) map[index] = event.target.value; else delete map[index];
      this.state.colorMap = map;
      this.state.colorMapUndo = null;
      this.afterColorMapChange();
      return;
    }
    if (event.target.matches('[data-qc-sample]')) {
      this.toggleSample(event.target.checked);
      return;
    }
    if (event.target.matches('[data-qc-destination]')) {
      this.state.destination = event.target.value;
      this.updatePrice();
      return;
    }
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    switch (input.name) {
      case 'qc-material':
        this.state.materialHandle = input.value;
        this.state.colorName = this.firstAvailableColor(input.value);
        this.renderSwatches();
        this.updatePreviewColors();
        break;
      case 'qc-color':
        this.state.colorName = input.value;
        this.updatePreviewColors();
        break;
      case 'qc-quality':
        this.state.qualityId = input.value;
        break;
      case 'qc-infill':
        this.state.infillId = input.value;
        break;
      case 'qc-finish':
        this.state.finishId = input.value;
        this.finishAutoSelected = false;
        this.toggleInsertsField();
        this.updatePreviewColors();
        break;
      case 'qc-delivery':
        this.state.rush = input.value === 'rush';
        break;
      default:
        if (input.matches('[data-qc-large-format]')) this.state.largeFormat = input.checked;
        else return;
    }
    this.runChecks();
    this.updatePrice();
  }

  setQuantity(quantity, { fromInput = false } = {}) {
    this.state.quantity = Math.max(1, Math.min(10000, quantity));
    if (!fromInput) {
      const input = this.els.params.querySelector('[data-qc-quantity-input]');
      if (input) input.value = String(this.state.quantity);
    }
    for (const button of this.els.params.querySelectorAll('[data-qc-qty]')) {
      button.setAttribute('aria-pressed', String(Number(button.dataset.qcQty) === this.state.quantity));
    }
    if (this.state.sample && this.state.quantity !== 1) this.state.sample = false;
    this.updateSampleOption();
    this.updatePrice();
  }

  /* ---------- upload & analyse ---------- */

  /**
   * Upload als transactie: elke upload krijgt een generatienummer. De vorige verwerking wordt
   * afgebroken en de oude staat (bestand, model, prijs) wordt gewist VOORDAT het nieuwe bestand
   * wordt gevalideerd, zodat een ongeldig vervangend bestand nooit de oude prijs laat staan.
   * Alleen het resultaat van de recentste generatie wordt geaccepteerd; tijdens de verwerking is
   * de koopknop vergrendeld.
   */
  async handleFile(file) {
    this.uploadGeneration += 1;
    const generation = this.uploadGeneration;
    this.cancelActiveParse();
    this.resetModel();
    this.quoteRequestFile = file;
    this.quoteRequestTransaction = null;
    const extension = fileExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      this.showFileError(this.t('error_file_type', { extension: extension ? `.${extension}` : '?' }), file.name);
      return;
    }
    const maxMb = Number(this.settings.max_file_mb) || 50;
    if (file.size > maxMb * BYTES_PER_MB) {
      this.showFileError(this.t('error_file_size', { max: maxMb, size: formatNumber(file.size / BYTES_PER_MB, 0) }), file.name);
      return;
    }
    this.setParsing(true);
    this.els.dropzone.querySelector('.qc__dropzone-title').textContent = file.name;
    if (extension === 'step' || extension === 'stp') this.addMessage('info', this.t('info_step_loading'));
    const job = startModelParse(file, (progress) => {
      if (generation === this.uploadGeneration) this.renderProgress(progress);
    });
    this.activeParse = job;
    try {
      const result = await job.promise;
      if (generation !== this.uploadGeneration) return; // verouderd resultaat (nieuwere upload of geannuleerd)
      this.els.progress.textContent = this.t('upload_parsing');
      if (!result.stats || !result.stats.triangleCount) throw new Error(this.t('error_no_triangles'));
      this.state.file = file;
      this.state.model = {
        positions: result.positions,
        stats: result.stats,
        objects: result.objects || [],
        format: result.format,
        warnings: result.warnings || [],
        colorIndex: result.colorIndex || null,
        palette: Array.isArray(result.palette) ? result.palette : [],
      };
      this.state.scale = 1;
      this.state.colorMap = {};
      this.state.colorMapUndo = null;
      this.state.sample = false;
      // Meerkleurig bestand (≥ 2 kleuren): afwerking "Meerkleurig (AMS)" voorselecteren; een eerdere
      // automatische keuze wordt bij een volgend eenkleurig bestand weer teruggezet.
      if (this.state.model.palette.length >= 2) {
        if (this.state.finishId !== 'multicolor') {
          this.selectFinish('multicolor');
          this.finishAutoSelected = true;
        }
      } else if (this.finishAutoSelected) {
        this.selectFinish('none');
        this.finishAutoSelected = false;
      }
      this.els.dropzone.classList.add('has-file');
      this.els.uploadButton.textContent = this.t('upload_replace');
      await this.showPreview();
      if (generation !== this.uploadGeneration) return;
      this.afterModelChange();
    } catch (error) {
      if (generation !== this.uploadGeneration || error instanceof ParseCancelledError) return;
      console.warn('[quote-configurator] Parsen mislukt', error);
      this.state.model = null;
      this.state.file = null;
      this.clearMessages();
      // Limiet (uitgepakte bytes, entries, driehoeken) of time-out: nette melding + offerte-route.
      const text = error && error.code === 'timeout'
        ? this.t('error_timeout', { seconds: error.seconds || WORKER_TIMEOUT_MS / 1000 })
        : error && error.code === 'limit'
          ? this.t('error_limit', { reason: this.humanizeError(error) })
          : this.t('error_parse', { format: formatLabel(extension), reason: this.humanizeError(error) });
      this.showFileError(text, file.name);
      this.els.dropzone.querySelector('.qc__dropzone-title').textContent = this.t('upload_title');
    } finally {
      if (generation === this.uploadGeneration) {
        this.activeParse = null;
        this.setParsing(false);
      }
    }
  }

  /** Vergrendelt/ontgrendelt de UI tijdens het lezen en parsen. */
  setParsing(parsing) {
    this.state.parsing = parsing;
    this.els.progress.hidden = !parsing;
    if (parsing) this.els.progress.textContent = this.t('upload_parsing');
    if (this.els.cancelButton) this.els.cancelButton.hidden = !parsing;
    this.els.dropzone.classList.toggle('is-busy', parsing);
    this.updatePrice();
  }

  cancelActiveParse() {
    if (this.activeParse) this.activeParse.cancel();
    this.activeParse = null;
  }

  /** Annuleren-knop: worker beëindigen, staat wissen; de volgende upload werkt normaal. */
  cancelUpload() {
    this.uploadGeneration += 1;
    this.cancelActiveParse();
    this.resetModel();
    this.setParsing(false);
    this.addMessage('info', this.t('upload_cancelled'));
  }

  /** Wist bestand, model, prijs en preview (lege beginstaat); meldingen worden apart gewist. */
  resetModel() {
    this.setParsing(false);
    this.quoteRequestFile = null;
    this.quoteRequestTransaction = null;
    this.state.file = null;
    this.state.model = null;
    this.state.quote = null;
    this.state.scale = 1;
    this.state.checks = [];
    // Nieuw bestand = nieuwe beoordeling: Expertcheck-keuze en goedkeur-vinkje gaan uit.
    this.addonsUi?.update({ expertcheck: false, approved: false });
    this.clearMessages();
    this.els.dropzone.classList.remove('has-file');
    this.els.dropzone.querySelector('.qc__dropzone-title').textContent = this.t('upload_title');
    this.els.uploadButton.textContent = this.t('upload_button');
    this.viewer?.clearModel();
    this.els.viewer.hidden = true;
    if (this.viewer) this.els.viewerNote.hidden = true;
    this.els.stats.hidden = true;
    this.els.unitHint.hidden = true;
    this.renderQuantities();
    this.updatePrice();
  }

  /** Voortgang in de dropzone: lezen (%), uitpakken, parsen (%), analyseren, STEP-lezer laden. */
  renderProgress({ stage, percent }) {
    const hasPercent = Number.isFinite(percent);
    const keys = {
      read: 'upload_progress',
      unzip: 'progress_unzip',
      parse: hasPercent ? 'progress_parse' : 'upload_parsing',
      analyze: 'progress_analyze',
      'step-load': 'progress_step_load',
      step: 'progress_step',
    };
    this.els.progress.textContent = this.t(keys[stage] || 'upload_parsing', { percent: hasPercent ? percent : 0 });
  }

  /** Technische foutmelding → nette Nederlandse zin zonder stacktrace; leeg wordt "onbekende fout". */
  humanizeError(error) {
    const raw = String((error && error.message) || error || '').trim();
    if (!raw) return this.t('error_unknown');
    const cleaned = raw.replace(/^(Error|TypeError|RangeError):\s*/i, '').replace(/\s+at\s+.*$/s, '').replace(/\.$/, '');
    if (/out of memory|allocation failed|Invalid array length|Array buffer allocation/i.test(cleaned)) return this.t('error_memory');
    return cleaned.charAt(0).toLowerCase() === cleaned.charAt(0) ? cleaned : cleaned.charAt(0).toLowerCase() + cleaned.slice(1);
  }

  /** Foutmelding met vervolgstap en knop "Offerte aanvragen" die het formulier opent met de bestandsnaam. */
  showFileError(text, fileName) {
    const item = document.createElement('div');
    item.className = 'qc__message qc__message--error qc__message--action';
    const paragraph = document.createElement('p');
    paragraph.textContent = `${text} ${this.t('error_next_steps')}`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'qc__button qc__button--small';
    button.textContent = this.t('cta_request_quote');
    button.addEventListener('click', () => this.openQuoteFormForFile(fileName, text));
    item.append(paragraph, button);
    this.els.messages.append(item);
  }

  openQuoteFormForFile(fileName, reason) {
    const { quoteForm, quoteReason } = this.els;
    quoteForm.hidden = false;
    quoteReason.textContent = this.t('quote_reason_file_error');
    for (const field of quoteForm.querySelectorAll('[data-qc-quote-field]')) {
      const key = field.dataset.qcQuoteField;
      if (key === 'Bestand') field.value = fileName || '';
      else if (key === 'Reden') field.value = `Bestand niet leesbaar: ${reason}`.slice(0, 500);
      else if (!this.state.model) field.value = '';
    }
    quoteForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
    quoteForm.querySelector('input[name="contact[name]"]')?.focus({ preventScroll: true });
  }

  async showPreview() {
    this.els.viewer.hidden = false;
    try {
      if (!this.viewer) {
        this.els.viewerNote.hidden = false;
        this.els.viewerNote.textContent = this.t('preview_loading');
        this.viewer = new ModelViewer({ canvas: this.els.canvas, labelsRoot: this.els.labels });
        await this.viewer.init();
        this.els.viewerNote.hidden = true;
      }
      const { positions, shownTriangles, colorIndex } = this.previewGeometry();
      this.previewColorIndex = colorIndex;
      this.viewer.setColor(this.selectedColorHex());
      this.viewer.setModel(positions, this.state.scale, this.previewVertexColors());
      const decimated = shownTriangles < this.state.model.stats.triangleCount;
      // Melding alleen voor dit model; een eerdere "toont x%"-melding mag niet blijven staan.
      this.els.viewerNote.hidden = !decimated;
      this.els.viewerNote.textContent = decimated
        ? this.t('preview_decimated', { percent: Math.max(1, Math.round((shownTriangles / this.state.model.stats.triangleCount) * 100)) })
        : '';
    } catch (error) {
      console.warn('[quote-configurator] 3D-weergave niet beschikbaar', error);
      this.viewer = null;
      this.els.viewerNote.hidden = false;
      this.els.viewerNote.textContent = this.t('preview_unavailable');
    }
  }

  /** Statistieken van het model in mm/cm, inclusief de gekozen schaal. */
  modelMetrics() {
    const { model, scale } = this.state;
    if (!model) return null;
    const { stats } = model;
    const sizeOf = (entry) => [0, 1, 2].map((axis) => (entry.bboxMax[axis] - entry.bboxMin[axis]) * scale);
    const size = sizeOf(stats);
    const volumeMm3 = stats.volumeMm3 * scale ** 3;
    const areaMm2 = stats.areaMm2 * scale ** 2;
    const overhangAreaMm2 = (Number(stats.overhangAreaMm2) || 0) * scale ** 2;
    const objects = (model.objects.length > 0 ? model.objects : [stats]).map((object, index) => {
      const objectSize = sizeOf(object);
      return {
        name: object.name || this.t('object_default_name', { index: index + 1 }),
        size: objectSize,
        maxDim: Math.max(...objectSize),
        volumeCm3: (object.volumeMm3 * scale ** 3) / 1000,
        volumeMm3: object.volumeMm3 * scale ** 3,
        bboxVolumeMm3: (Number(object.bboxVolumeMm3) || 0) * scale ** 3,
        closed: object.closed,
        openEdges: object.openEdges,
        overlap: Boolean(object.overlap),
        duplicatesRemoved: Boolean(object.duplicatesRemoved),
        ambiguous: Boolean(object.ambiguous),
        ambiguousReason: String(object.ambiguousReason || ''),
        boundaryLoops: object.boundaryLoops,
        openLoops: object.openLoops,
        holeAreaMm2: (Number(object.holeAreaMm2) || 0) * scale ** 2,
        areaMm2: object.areaMm2 * scale ** 2,
        signedVolumeMm3: (Number(object.signedVolumeMm3) || 0) * scale ** 3,
        nonManifoldEdges: object.nonManifoldEdges,
        edgeCount: object.edgeCount,
        volumeInconsistent: Boolean(object.volumeInconsistent),
        volumeEstimated: Boolean(object.volumeEstimated),
        checkPartial: Boolean(object.checkPartial),
        repaired: {
          flippedTriangles: Number(object.repaired?.flippedTriangles) || 0,
          closedLoops: Number(object.repaired?.closedLoops) || 0,
          cappedAreaMm2: (Number(object.repaired?.cappedAreaMm2) || 0) * scale ** 2,
        },
        triangleCount: object.triangleCount,
      };
    });
    return {
      size,
      maxDim: Math.max(...size),
      volumeCm3: volumeMm3 / 1000,
      areaCm2: areaMm2 / 100,
      /** Overhang-oppervlak ÷ totaal oppervlak (0..1): supports-proxy van de prijs-engine. */
      overhangRatio: areaMm2 > 0 ? overhangAreaMm2 / areaMm2 : 0,
      thicknessMm: areaMm2 > 0 ? (2 * volumeMm3) / areaMm2 : 0,
      triangleCount: stats.triangleCount,
      closed: stats.closed,
      openEdges: stats.openEdges,
      volumeEstimated: objects.some((object) => object.volumeEstimated),
      checkPartial: objects.some((object) => object.checkPartial),
      repaired: {
        flippedTriangles: objects.reduce((sum, object) => sum + object.repaired.flippedTriangles, 0),
        closedLoops: objects.reduce((sum, object) => sum + object.repaired.closedLoops, 0),
        cappedAreaMm2: objects.reduce((sum, object) => sum + object.repaired.cappedAreaMm2, 0),
      },
      objectCount: objects.length,
      objects,
    };
  }

  applyScale(scale) {
    this.state.scale = scale;
    if (this.viewer && this.state.model) {
      const { positions, colorIndex } = this.previewGeometry();
      this.previewColorIndex = colorIndex;
      this.viewer.setModel(positions, scale, this.previewVertexColors());
    }
    this.afterModelChange();
  }

  /** Vereenvoudigde weergavegeometrie, één keer per model berekend (schaal verandert de posities niet). */
  previewGeometry() {
    const model = this.state.model;
    model.preview ??= this.previewModule.decimateForPreview(model.positions, model.stats.triangleCount, model.colorIndex);
    return model.preview;
  }

  /* ---------- kleur in de preview ---------- */

  /** Hex van de gekozen swatch (metaobject material.colors); zonder swatch lichtgrijs. */
  selectedColorHex() {
    const palette = this.state.model?.palette || [];
    if (palette.length >= 2 && effectiveColorCount(palette, this.state.colorMap) === 1) return applyColorMap(palette, this.state.colorMap)[0];
    const material = this.materialByHandle(this.state.materialHandle);
    const color = material?.colors?.find((entry) => entry.name === this.state.colorName);
    return color?.hex || '#c4cad0';
  }

  /** Vertexkleuren voor de preview: alleen bij afwerking "Meerkleurig (AMS)" en een meerkleurig bestand. */
  previewVertexColors() {
    const model = this.state.model;
    if (!model || !this.previewColorIndex) return null;
    const palette = applyColorMap(model.palette, this.state.colorMap);
    // Alles naar één kleur toegewezen → effen model in die kleur; anders per driehoek de (toegewezen) kleur.
    if (palette.length >= 2 && effectiveColorCount(model.palette, this.state.colorMap) === 1) return null;
    if (this.state.finishId !== 'multicolor' && palette.length >= 2) return buildVertexColors(this.previewColorIndex, palette, this.selectedColorHex());
    return buildVertexColors(this.previewColorIndex, palette, this.selectedColorHex());
  }

  /** Swatch/materiaal/afwerking gewijzigd → kleur van het model in de viewer direct bijwerken. */
  updatePreviewColors() {
    if (!this.viewer || !this.state.model) return;
    this.viewer.setColor(this.selectedColorHex());
    this.viewer.setVertexColors(this.previewVertexColors());
  }

  /** Zet de afwerking (radio + state), bijv. AMS bij een meerkleurig bestand. */
  /** Het veld "Aantal inserts per stuk" alleen bij afwerking Inserts. */
  toggleInsertsField() {
    const wrap = this.els.params.querySelector('[data-qc-inserts]');
    if (wrap) wrap.hidden = this.state.finishId !== 'inserts';
  }

  selectFinish(finishId) {
    if (!this.config.finishes.some((finish) => finish.id === finishId)) return;
    this.state.finishId = finishId;
    const radio = this.els.params.querySelector(`input[name="qc-finish"][value="${finishId}"]`);
    if (radio) radio.checked = true;
  }

  afterModelChange() {
    this.renderStats();
    this.renderUnitHint();
    this.renderPaletteMap();
    this.runChecks();
    this.renderQuantities();
    this.setQuantity(this.state.quantity);
    if (this.state.model) {
      this.stepUnlocked = Math.max(this.stepUnlocked || 2, 3);
      this.updateSteps();
    } else {
      this.stepUnlocked = 2;
      this.stepOpen = 2;
      this.updateSteps();
    }
  }

  renderStats() {
    const metrics = this.modelMetrics();
    const { stats } = this.els;
    if (!metrics) {
      stats.hidden = true;
      return;
    }
    const dims = metrics.size.map((v) => formatNumber(v, 1)).join(' × ');
    let closedText = this.t('stats_closed_unknown');
    if (metrics.closed === true) closedText = this.t('stats_closed_yes');
    if (metrics.closed === true && metrics.repaired.closedLoops > 0) closedText = this.t('stats_closed_repaired', { count: formatNumber(metrics.repaired.closedLoops, 0) });
    if (metrics.closed === false) closedText = this.t('stats_closed_no', { count: formatNumber(metrics.openEdges, 0) });
    if (metrics.checkPartial) closedText += ` (${this.t('stats_closed_partial')})`;
    const multi = metrics.objectCount > 1;
    const rows = [
      ...(multi ? [[this.t('stats_objects'), formatNumber(metrics.objectCount, 0)]] : []),
      [multi ? this.t('stats_dimensions_total') : this.t('stats_dimensions'), `${dims} mm`],
      [this.t('stats_volume'), metrics.objects.some(object => object.overlap) ? 'Unievolume wordt handmatig berekend' : `${formatNumber(metrics.volumeCm3, 2)} cm³${metrics.volumeEstimated ? ` (${this.t('stats_volume_estimated')})` : ''}`],
      [this.t('stats_area'), `${formatNumber(metrics.areaCm2, 1)} cm²`],
      [this.t('stats_triangles'), formatNumber(metrics.triangleCount, 0)],
      [this.t('stats_closed'), closedText],
      [this.t('stats_overhang'), this.overhangText(metrics)],
      // Eerlijk benoemd: 2 × volume ÷ oppervlak is één gemiddelde voor het hele model, geen lokale meting.
      ...(metrics.thicknessMm > 0 ? [[this.t('stats_wall'), this.t('stats_wall_value', { thickness: formatNumber(metrics.thicknessMm, 1) })]] : []),
    ];
    const objectList = multi
      ? `<details class="qc__objects"><summary>${esc(this.t('stats_objects_list', { count: metrics.objectCount }))}</summary><ol class="qc__objects-list">${metrics.objects
          .map((object) => `<li>${esc(object.name)} · ${esc(object.size.map((v) => formatNumber(v, 1)).join(' × '))} mm · ${object.overlap ? 'unievolume op offerte' : `${esc(formatNumber(object.volumeCm3, 2))} cm³`}</li>`)
          .join('')}</ol></details>`
      : '';
    const palette = this.state.model?.palette || [];
    const colorRow = palette.length > 0
      ? `<div><dt>${esc(this.t('stats_colors'))}</dt><dd>${palette.map((hex) => `<span class="qc__color-dot" style="--swatch: ${esc(hex)}" title="${esc(hex)}"></span>`).join('')} ${esc(formatNumber(palette.length, 0))}</dd></div>`
      : '';
    stats.hidden = false;
    stats.innerHTML = `
      <h3 class="qc__stats-title">${esc(multi ? this.t('stats_title_multi', { count: metrics.objectCount }) : this.t('stats_title'))}</h3>
      <dl class="qc__stats-list">${rows.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('')}${colorRow}</dl>${objectList}`;
  }

  /** "≈ 42% van het oppervlak steiler dan 45° → supports (+12% machinetijd)" of "…; geen supports-opslag". */
  overhangText(metrics) {
    const percent = formatNumber(metrics.overhangRatio * 100, 0);
    const supports = this.pricing.needsSupports(metrics.overhangRatio, this.config);
    return supports
      ? this.t('stats_overhang_value', { percent, surcharge: formatNumber(this.config.supportSurchargePercent, 0) })
      : this.t('stats_overhang_none', { percent });
  }

  renderUnitHint() {
    const metrics = this.modelMetrics();
    const { unitHint } = this.els;
    unitHint.hidden = !metrics;
    if (!metrics) return;
    const tiny = metrics.maxDim < UNIT_HINT_MAX_MM && this.state.scale === 1;
    const buttons = [];
    if (tiny) {
      for (const option of UNIT_SCALE_OPTIONS) {
        buttons.push(`<button type="button" class="qc__button" data-qc-scale="${option.scale}">${esc(this.t(option.key))}</button>`);
      }
    }
    for (const factor of [0.5, 0.75, 1.5, 2]) {
      buttons.push(`<button type="button" class="qc__button" data-qc-scale-rel="${factor}">${factor < 1 ? '−' : '+'}${Math.round(Math.abs(factor - 1) * 100)}%</button>`);
    }
    if (this.state.scale !== 1) buttons.push(`<button type="button" class="qc__button" data-qc-scale="1">${esc(this.t('unit_reset'))}</button>`);
    const hint = tiny ? this.t('unit_hint', { size: formatNumber(metrics.maxDim, 2) }) : this.t('size_hint');
    const factorNote = this.state.scale === 1 ? '' : ` <span class="qc__size-factor">(× ${formatNumber(this.state.scale, 2)})</span>`;
    unitHint.innerHTML = `<p>${esc(hint)}</p><label class="qc__size">${esc(this.t('size_label'))} <input type="number" inputmode="decimal" min="1" step="0.1" value="${metrics.maxDim.toFixed(1)}" data-qc-size-mm aria-label="${esc(this.t('size_label'))}"> mm${factorNote}</label><div class="qc__unit-buttons">${buttons.join('')}</div>`;
  }

  /* ---------- checks ---------- */

  runChecks() {
    const metrics = this.modelMetrics();
    const checks = [];
    if (metrics) {
      const standardMax = Number(this.settings.build_volume_mm) || 250;
      const largeMax = Number(this.settings.large_format_mm) || 320;
      // Bouwvolume per object: een plaat met meerdere onderdelen mag groter zijn dan één printer.
      const multi = metrics.objectCount > 1;
      const tooLarge = metrics.objects.filter((object) => object.maxDim > largeMax);
      const needsLarge = metrics.objects.filter((object) => object.maxDim > standardMax && object.maxDim <= largeMax);
      const describe = (object) => (multi ? `${object.name}: ${object.size.map((v) => formatNumber(v, 0)).join(' × ')}` : object.size.map((v) => formatNumber(v, 0)).join(' × '));
      if (tooLarge.length > 0) {
        checks.push({ level: 'error', blocking: true, text: this.t('check_build_volume_blocked', { size: describe(tooLarge[0]), max: largeMax }) });
      } else if (needsLarge.length > 0 && !this.state.largeFormat) {
        checks.push({ level: 'error', blocking: true, text: this.t('check_build_volume', { size: describe(needsLarge[0]), max: standardMax }) });
      }
      checks.push(...this.meshPolicyChecks(metrics));
      const paletteSize = this.state.model?.palette?.length || 0;
      const maxColors = this.colorTools?.MAX_PRINT_COLORS || 9;
      const effectiveColors = paletteSize >= 2 ? effectiveColorCount(this.state.model.palette, this.state.colorMap) : 0;
      if (effectiveColors > maxColors) {
        checks.push({ level: 'error', blocking: true, reason: 'kleuren', text: this.t('check_too_many_colors', { count: effectiveColors, max: maxColors }) });
      } else if (paletteSize >= 2) {
        checks.push({ level: 'info', text: this.t('info_multicolor', { count: paletteSize }) });
      }
      for (const warning of this.state.model?.warnings || []) checks.push({ level: 'info', text: warning });
      // Materiaalgrens uit het metaobject (PLA/PETG 1,2 mm, ASA/TPU/PA-CF 1,5 mm); alleen zonder waarde de fallback.
      const material = this.materialByHandle(this.state.materialHandle);
      const minWall = parseMillimeters(material?.minWall) || THIN_WALL_MM;
      if (metrics.thicknessMm > 0 && metrics.thicknessMm < minWall) {
        checks.push({
          level: 'warning',
          text: this.t('check_thin_walls', {
            thickness: formatNumber(metrics.thicknessMm, 1),
            material: material?.name || '',
            min_wall: formatNumber(minWall, 1),
          }),
        });
      }
      if (metrics.volumeCm3 < TINY_VOLUME_CM3) checks.push({ level: 'warning', text: this.t('check_tiny', { volume: formatNumber(metrics.volumeCm3, 3) }) });
      if (metrics.volumeCm3 > HUGE_VOLUME_CM3) checks.push({ level: 'warning', text: this.t('check_huge', { volume: formatNumber(metrics.volumeCm3, 0) }) });
    }
    this.state.checks = checks;
    this.clearMessages();
    for (const check of checks) this.addMessage(check.level, check.text);
  }

  get isBlocked() {
    return this.state.checks.some((check) => check.blocking);
  }

  clearMessages() {
    this.els.messages.replaceChildren();
  }

  addMessage(level, text) {
    if (!text) return;
    const item = document.createElement('p');
    item.className = `qc__message qc__message--${level}`;
    item.textContent = text;
    this.els.messages.append(item);
  }

  showCtaError(text) {
    this.els.ctaError.hidden = !text;
    this.els.ctaError.textContent = text || '';
  }

  /* ---------- prijs ---------- */

  quoteInput(quantity = this.state.quantity) {
    const metrics = this.modelMetrics();
    return {
      volumeCm3: metrics ? metrics.volumeCm3 : 0,
      objectVolumesCm3: metrics ? metrics.objects.map((object) => object.volumeCm3) : [0],
      materialHandle: this.state.materialHandle,
      qualityId: this.state.qualityId,
      infillId: this.state.infillId,
      finishId: this.state.finishId,
      insertCount: this.state.insertCount,
      overhangRatio: metrics ? metrics.overhangRatio : 0,
      quantity,
      rush: this.state.rush,
      // Spoedminimum (€ 9,95) één keer per bestelling: al gedekt door een spoedregel in de winkelwagen?
      rushMinimumWaived: this.state.cartRushSurchargeCents >= Math.round(this.config.rushMinimum * 100),
      largeFormat: this.state.largeFormat,
      includeOrderFee: !this.state.orderFeeInCart,
    };
  }

  updatePrice() {
    const { els } = this;
    const { includeOrderFee, rushMinimumWaived, ...choices } = this.quoteInput();
    const approvalKey = JSON.stringify([this.uploadGeneration, this.state.colorName, this.state.scale, choices]);
    if (approvalKey !== this.approvalKey) {
      this.addonsUi?.update({ approved: false });
      this.approvalKey = approvalKey;
    }
    if (!this.state.model || !this.state.materialHandle) {
      this.state.quote = null;
      els.total.textContent = '–';
      els.total.closest('[data-qc-pricebar]')?.setAttribute('data-qc-pricebar', 'waiting');
      els.unitPrice.textContent = this.t('price_waiting');
      els.breakdown.hidden = true;
      if (els.addonsStep) els.addonsStep.hidden = true;
      els.addToCart.disabled = true;
      els.quoteForm.hidden = !this.querySelector('.qc__form-success, .qc__form-error');
      this.renderDelivery(null);
      return;
    }
    if (this.isBlocked) {
      // No price or summed volume for an unproven union. Keep the file available for the request.
      this.state.quote = null;
      els.total.textContent = 'Op offerte';
      els.total.closest('[data-qc-pricebar]')?.setAttribute('data-qc-pricebar', 'ready');
      els.unitPrice.textContent = 'Wij controleren het bestand en berekenen de prijs handmatig.';
      els.breakdown.hidden = true;
      if (els.addonsStep) els.addonsStep.hidden = true;
      els.addToCart.disabled = true;
      els.addToCart.hidden = true;
      els.quoteForm.hidden = false;
      const reason = this.state.checks.filter(check => check.blocking).map(check => check.text).join(' ');
      els.quoteReason.textContent = reason;
      const values = { Bestand: this.state.file?.name || '', Reden: reason, Indicatieprijs: 'Op offerte',
        Materiaal: this.state.materialHandle, Kleur: this.state.colorName, 'Aantal stuks': this.state.quantity,
        Levertijd: 'Levertijd op offerte', Bestemming: this.state.destination || 'NL',
        ...((this.state.model?.palette?.length || 0) >= 2 ? { 'Kleuren in bestand': String(this.state.model.palette.length), Kleurtoewijzing: this.colorMapProperty() } : {}),
        ...(this.state.colorWish ? { Kleurwens: this.state.colorWish } : {}) };
      for (const field of els.quoteForm.querySelectorAll('[data-qc-quote-field]')) field.value = values[field.dataset.qcQuoteField] ?? '';
      return;
    }
    let quote;
    try {
      quote = this.pricing.calculateQuote(this.quoteInput(), this.config);
      // Levertijd-keuze volgt de printuren; valt spoed weg (drukte/uren), dan zonder spoed opnieuw rekenen.
      const rushBefore = this.state.rush;
      this.renderDelivery(quote);
      if (rushBefore !== this.state.rush) quote = this.pricing.calculateQuote(this.quoteInput(), this.config);
    } catch (error) {
      console.error('[quote-configurator] Prijsberekening mislukt', error);
      this.showCtaError(String(error.message || error));
      return;
    }
    this.state.quote = quote;
    const incl = this.state.showIncl;
    // Stap "Extra's" eerst: de gekozen Expertcheck (€ 9,95 of gratis) hoort in het getoonde totaal.
    this.renderAddonsStep(quote);
    const addonIncl = this.expertcheckPriceCents(quote) / 100;
    const total = incl ? this.pricing.roundCents(quote.totalIncl + addonIncl) : this.pricing.roundCents(quote.totalExcl + addonIncl / (1 + this.config.vatRate));
    els.total.textContent = `${this.money(total)} ${this.t(incl ? 'vat_incl' : 'vat_excl')}`;
    els.total.closest('[data-qc-pricebar]')?.setAttribute('data-qc-pricebar', 'ready');
    els.unitPrice.textContent = this.t('price_per_unit', { price: this.money(incl ? quote.unitPriceIncl : quote.unitPriceExcl) });
    this.renderBreakdown(quote);
    this.renderQuantities();
    this.updateCta(quote);
  }

  /** Stap "Extra's" (Expertcheck) in de prijsbalk; prijs volgt de gratis-regels (€150 / 25+ stuks). */
  renderAddonsStep(quote) {
    const { addonsStep } = this.els;
    if (!this.addons || !addonsStep) return;
    const metrics = this.modelMetrics();
    const hasWarnings = this.quoteFlag(metrics) !== '' || this.state.checks.some((check) => check.level === 'warning');
    this.addonsUi ??= this.addons.renderAddonsStep(addonsStep, {
      variantId: this.dataset.expertcheckVariantId,
      idPrefix: `qc-${this.dataset.sectionId}`,
      // Aan-/uitzetten van de Expertcheck verandert het getoonde totaal.
      onChange: () => this.updatePrice(),
    });
    this.addonsUi.update({ ...this.expertcheckContext(quote), fileCount: 1, hasWarnings });
    addonsStep.hidden = false;
  }

  /** Gratis-grens (€150) per bestelling: deze regel plus maatwerk dat al in de winkelwagen staat. */
  expertcheckContext(quote) {
    return { subtotalCents: quote.lineTotalCents + this.state.cartQuoteTotalCents, quantity: quote.quantity };
  }

  /** Prijs van de gekozen Expertcheck in centen (0 = niet gekozen of gratis). */
  expertcheckPriceCents(quote) {
    if (!this.addons || !quote) return 0;
    return this.addons.expertcheckState(this.addonsUi?.getState(), this.expertcheckContext(quote)).priceCents;
  }

  /** Factor om bedragen in parameter-basis om te zetten naar de gekozen btw-weergave. */
  displayFactor() {
    const { showIncl } = this.state;
    const { parametersIncludeVat, vatRate } = this.config;
    if (showIncl === parametersIncludeVat) return 1;
    return showIncl ? 1 + vatRate : 1 / (1 + vatRate);
  }

  renderBreakdown(quote) {
    const b = quote.breakdown;
    const factor = this.displayFactor();
    const show = (amount) => this.money(this.pricing.roundCents(amount * factor));
    const rows = [
      ...(b.objectCount > 1 ? [[this.t('breakdown_objects', { count: b.objectCount }), '']] : []),
      [this.t('breakdown_material', { grams: formatNumber(quote.weightGrams, 1) }), show(b.materialCost)],
      [
        quote.speedFactor !== 1
          ? this.t('breakdown_machine', { hours: formatNumber(quote.baseHours, 2), factor: formatNumber(quote.speedFactor, 2) })
          : this.t('breakdown_machine_plain', { hours: formatNumber(quote.baseHours, 2) }),
        show(b.machineCost),
      ],
    ];
    if (b.supportCost) rows.push([this.t('breakdown_supports', { percent: formatNumber(b.supportPercent, 0) }), `+ ${show(b.supportCost)}`]);
    if (b.multicolorTimeCost) rows.push([this.t('breakdown_multicolor_time', { percent: formatNumber(b.multicolorPercent, 0) }), `+ ${show(b.multicolorTimeCost)}`]);
    if (b.minimumApplied) rows.push([b.objectCount > 1 ? this.t('breakdown_minimum_objects') : this.t('breakdown_minimum'), show(b.baseUnit)]);
    if (b.largeFormatSurcharge) rows.push([this.t('breakdown_large_format'), `+ ${show(b.largeFormatSurcharge)}`]);
    if (b.finishCost) rows.push([quote.insertCount > 0 ? this.t('finish_inserts_label', { count: quote.insertCount }) : this.t('breakdown_finish'), `+ ${show(b.finishCost)}`]);
    rows.push([this.t('breakdown_unit'), show(b.unitPrice)]);
    rows.push([this.t('breakdown_line', { quantity: quote.quantity }), show(b.lineBeforeTier)]);
    rows.push([this.t('breakdown_print_hours', { quantity: quote.quantity }), this.t('breakdown_print_hours_value', { hours: formatNumber(quote.printHours, 1) })]);
    if (b.tierDiscount) rows.push([this.t('breakdown_tier', { percent: b.tierPercent }), `− ${show(b.tierDiscount)}`]);
    if (b.rushSurcharge) rows.push([b.rushMinimumApplied ? this.t('breakdown_rush_minimum', { minimum: this.money(this.config.rushMinimum) }) : this.t('breakdown_rush'), `+ ${show(b.rushSurcharge)}`]);
    rows.push(
      this.state.orderFeeInCart
        ? [this.t('breakdown_order_fee_in_cart'), this.money(0)]
        : [this.t('breakdown_order_fee'), `+ ${show(b.orderFee)}`],
    );
    const expertcheck = this.addons?.expertcheckState(this.addonsUi?.getState(), this.expertcheckContext(quote)).selected;
    const addonCents = this.expertcheckPriceCents(quote);
    if (expertcheck) rows.push([this.t('breakdown_expertcheck'), addonCents > 0 ? `+ ${show(addonCents / 100)}` : this.t('breakdown_expertcheck_free')]);
    const addonIncl = addonCents / 100;
    const vat = this.pricing.roundCents(quote.vatAmount + (addonIncl - addonIncl / (1 + this.config.vatRate)));
    const total = this.state.showIncl ? this.pricing.roundCents(quote.totalIncl + addonIncl) : this.pricing.roundCents(quote.totalExcl + addonIncl / (1 + this.config.vatRate));
    if (this.state.showIncl) rows.push([this.t('breakdown_vat', { percent: Math.round(this.config.vatRate * 100) }), this.money(vat)]);
    rows.push([this.t('breakdown_total'), this.money(total)]);
    this.els.breakdownList.innerHTML = rows
      .map(([k, v], index) => `<div${index === rows.length - 1 ? ' class="qc__breakdown-total"' : ''}><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`)
      .join('');
    this.els.breakdown.hidden = false;
  }

  /** Offertedrempel per bestelling: deze regel + maatwerk in de winkelwagen (bedrag én printuren). */
  orderDecision(quote) {
    return this.pricing.orderQuoteDecision(
      quote,
      { cartQuoteTotalIncl: this.state.cartQuoteTotalCents / 100, cartPrintHours: this.state.cartPrintHours },
      this.config,
    );
  }

  /** Vraagt deze bestelling een offerte (blokkade, drempel of capaciteit)? */
  needsQuote(quote = this.state.quote) {
    return !quote || this.isBlocked || this.orderDecision(quote).needsQuote;
  }

  updateCta(quote) {
    const blocked = this.isBlocked;
    const decision = this.orderDecision(quote);
    const overThreshold = decision.needsQuote;
    const needsQuote = blocked || overThreshold;
    this.els.addToCart.disabled = needsQuote || !this.dataset.variantId || this.state.parsing || this.addInFlight;
    this.els.addToCart.hidden = needsQuote;
    this.els.quoteForm.hidden = !needsQuote && !this.querySelector('.qc__form-success, .qc__form-error');
    this.els.quoteReason.textContent = blocked
      ? this.t('quote_reason_blocked')
      : overThreshold
        ? this.quoteReasonText(decision)
        : '';
    if (!this.dataset.variantId && !needsQuote) this.showCtaError(this.t('error_product_missing'));
    else this.showCtaError('');
    const blockReasons = this.state.checks.filter((check) => check.blocking).map((check) => check.reason || 'bouwvolume');
    this.fillQuoteForm(quote, blocked ? `Check geblokkeerd (${[...new Set(blockReasons)].join(', ')})` : overThreshold ? `Boven drempel (${decision.reason === 'hours' ? 'printuren' : 'bedrag + printuren'})` : '');
  }

  /** Reden voor de offerte-route: bedrag-plus-uren of boven de dagcapaciteit. */
  quoteReasonText(decision) {
    if (decision.reason === 'hours') {
      return this.t('quote_reason_hours', { hours: formatNumber(decision.orderPrintHours, 1), max: formatNumber(Math.min(40, this.config.quoteMaxHours || 40), 0) });
    }
    return this.t('quote_reason_threshold', {
      threshold: this.money(this.config.quoteThreshold),
      hours: formatNumber(this.config.quoteThresholdHours, 0),
      order_hours: formatNumber(decision.orderPrintHours, 1),
    });
  }

  /* ---------- properties, winkelwagen, offerte ---------- */

  lineProperties(quote) {
    const metrics = this.modelMetrics();
    const material = this.materialByHandle(this.state.materialHandle);
    const label = (list, id) => list.find((entry) => entry.id === id)?.label || id;
    return {
      Bestand: this.state.file?.name || '',
      Materiaal: material?.name || '',
      Kleur: this.state.colorName || '–',
      Kwaliteit: this.t(`quality_${this.state.qualityId}`),
      Vulling: this.t(`infill_${this.state.infillId}`),
      Afwerking: quote.insertCount > 0 ? this.t('finish_inserts_label', { count: quote.insertCount }) : label(this.config.finishes, this.state.finishId),
      ...(quote.supports ? { Supports: `Ja (overhang ${formatNumber((metrics?.overhangRatio || 0) * 100, 0)}%, +${formatNumber(quote.breakdown.supportPercent, 0)}% machinetijd)` } : {}),
      'Aantal stuks': String(quote.quantity),
      [PRINT_HOURS_PROPERTY]: `${formatNumber(quote.printHours, 1)} u`,
      Levertijd: this.deliveryLabel(quote),
      Bestemming: this.state.destination || 'NL',
      Afmetingen: metrics ? `${metrics.size.map((v) => formatNumber(v, 1)).join(' × ')} mm` : '',
      'Objecten in bestand': String(metrics ? metrics.objectCount : 1),
      ...((this.state.model?.palette?.length || 0) >= 2 ? { 'Kleuren in bestand': String(this.state.model.palette.length), Kleurtoewijzing: this.colorMapProperty() } : {}),
      ...(this.state.colorWish ? { Kleurwens: this.state.colorWish } : {}),
      ...(this.state.sample ? { Proefexemplaar: this.t('sample_property', { count: this.state.sampleForQuantity || SAMPLE_FROM_QUANTITY }) } : {}),
      'Gewicht (g)': formatNumber(quote.weightGrams, 1),
      'Prijs per stuk': this.money(quote.unitPriceIncl),
      ...(this.quoteFlag(metrics) ? { _quote_flag: this.quoteFlag(metrics) } : {}),
      ...(quote.breakdown.rushSurcharge > 0 ? { [RUSH_CENTS_PROPERTY]: String(Math.round(quote.breakdown.rushSurcharge * 100)) } : {}),
      _quote_version: quote.version,
      // Regeltotaal zonder orderkosten; de orderkosten zijn een aparte regel (cart-order-fee.js).
      _quote_total_cents: String(quote.lineTotalCents),
      _quote_id: this.cartModule.buildQuoteId(),
      _quote_check: this.cartModule.buildQuoteCheck({
        totalCents: quote.lineTotalCents,
        fileName: this.state.file?.name || '',
        materialName: material?.name || '',
      }),
    };
  }

  /** "Standaard · verzonden op … · bezorgd …" of "Spoed · …" voor de orderregel (datums gelden bij afronden vandaag). */
  deliveryLabel(quote) {
    return `${this.state.rush ? 'Spoed' : 'Standaard'} · ${this.deliveryEstimate({ quote })} (bij afronden vandaag)`;
  }

  /**
   * Beleid ná de reparatiestap (assets/quote-checks.js): gedichte gaatjes, resterende kleine gaten of een
   * niet volledig gecontroleerd groot model → waarschuwing + `_quote_flag`; grote gaten, onherstelbare randen,
   * onzekere insluiting, geschat volume of geen check → blokkade (offerte-route, gratis reparatiepoging).
   * @returns {Array<{ level: string, blocking?: boolean, text: string }>}
   */
  meshPolicyChecks(metrics) {
    const assessment = this.checks.assessModel(metrics.objects);
    const multi = metrics.objectCount > 1;
    const partName = (entry) => (multi && entry.name ? `${entry.name}: ` : '');
    const checks = [];
    for (const entry of assessment.blockedParts) {
      const object = metrics.objects.find((candidate) => candidate.name === entry.name) || {};
      const detailKey = `ambiguous_reason_${object.ambiguousReason || 'unknown'}`;
      const vars = {
        part: partName(entry),
        count: formatNumber(object.openEdges || 0, 0),
        percent: formatNumber(entry.holeRatio * 100, 1),
        detail: this.i18n[detailKey] ? this.t(detailKey) : this.t('ambiguous_reason_unknown'),
      };
      checks.push({ level: 'error', blocking: true, reason: entry.reason, text: entry.reason === 'overlap' ? 'Onderdelen overlappen; wij berekenen de unie handmatig. Vraag een offerte aan.' : this.t(`check_blocked_${entry.reason}`, vars) });
    }
    if (assessment.blockedParts.length > 0) return checks;
    const flagText = {
      duplicates_removed: () => 'Dubbele driehoeken verwijderd; volume en prijs tellen deze maar één keer.',
      overlap_union: () => 'Onderdelen overlappen elkaar; de prijs is berekend op de samengevoegde inhoud (unie, nauwkeurigheid ±2%). Wij controleren dit in de slicer.',
      [this.checks.QUOTE_FLAG_REPAIRED]: () => this.t(metrics.repaired.closedLoops > 0 ? 'check_repaired' : 'check_reoriented', {
        count: formatNumber(metrics.repaired.closedLoops, 0),
        triangles: formatNumber(metrics.repaired.flippedTriangles, 0),
      }),
      [this.checks.QUOTE_FLAG_OPEN]: () => this.t('check_not_closed', { count: formatNumber(metrics.objects.reduce((sum, object) => sum + (object.openEdges || 0), 0), 0) }),
      [this.checks.QUOTE_FLAG_PARTIAL]: () => this.t('check_partial'),
    };
    for (const flag of assessment.flags) {
      if (flagText[flag]) checks.push({ level: 'warning', flag, text: flagText[flag]() });
    }
    return checks;
  }

  /** Interne markering voor staff (`_quote_flag`, kommagescheiden): mesh_repaired, mesh_open en/of check_partial; geblokkeerde modellen halen de winkelwagen nooit. */
  quoteFlag(metrics) {
    if (!metrics) return '';
    return this.checks.assessModel(metrics.objects).flag;
  }

  bindQuoteRequest() {
    const form = this.els.quoteForm.querySelector('form');
    if (!form) return;
    const status = form.querySelector('[data-qc-request-status]');
    if (form.querySelector('.qc__form-success')) {
      try {
        const id = sessionStorage.getItem('tdp-quote-request');
        if (id) { status.hidden = false; status.textContent = `Aanvraagnummer ${id}, bevestiging per e-mail.`; }
      } catch { /* Storage is optional. */ }
    }
    let ready = false, busy = false;
    form.addEventListener('submit', async (event) => {
      if (ready) { ready = false; return; }
      event.preventDefault();
      if (busy) return;
      const file = this.quoteRequestFile || this.state.file;
      if (!file) { status.hidden = false; status.textContent = 'Upload eerst het bestand dat bij je aanvraag hoort.'; return; }
      busy = true;
      const button = event.submitter || form.querySelector('[type="submit"]');
      button.disabled = true;
      status.hidden = false;
      status.textContent = 'Bestand wordt aan je aanvraag gekoppeld…';
      const generation = this.uploadGeneration;
      const choices = JSON.stringify([this.quoteInput(), this.state.colorName]);
      try {
        const module = await import(new URL(this.dataset.requestSrc, document.baseURI).href);
        const transfer = await import(new URL(this.dataset.fileTransferSrc, document.baseURI).href);
        const transaction = this.quoteRequestTransaction ??= { id: module.requestNumber(file) };
        let result;
        if (file.size > transfer.CHUNK_BYTES) {
          // Groot bestand: comprimeren en zo nodig in delen; de aanvraag krijgt alle links.
          transaction.prepared ??= await transfer.prepareFileProperties(file, { variantId: this.dataset.requestVariantId,
            cartUrl: this.dataset.cartUrl, addUrl: this.dataset.cartAddUrl, updateUrl: this.dataset.cartUpdateUrl,
            onProgress: (text) => { status.textContent = text; } });
          const prepared = transaction.prepared;
          if (prepared.mode === 'parts') {
            const links = Object.entries(prepared.properties).filter(([key]) => key.startsWith('Bestand deel ')).map(([, url]) => url);
            result = { id: transaction.id, url: `${file.name} (${links.length} delen, worden automatisch samengevoegd):\n${links.join('\n')}` };
          } else {
            result = await module.uploadRequestFile({ transaction, file: prepared.properties['Bestand upload'], variantId: this.dataset.requestVariantId,
              cartUrl: this.dataset.cartUrl, addUrl: this.dataset.cartAddUrl, updateUrl: this.dataset.cartUpdateUrl });
          }
        } else {
          result = await module.uploadRequestFile({ transaction, file, variantId: this.dataset.requestVariantId,
            cartUrl: this.dataset.cartUrl, addUrl: this.dataset.cartAddUrl, updateUrl: this.dataset.cartUpdateUrl });
        }
        if (generation !== this.uploadGeneration || choices !== JSON.stringify([this.quoteInput(), this.state.colorName])) {
          throw new Error('Je bestand of keuzes zijn gewijzigd. Controleer je aanvraag en klik opnieuw.');
        }
        form.querySelector('[data-qc-request-id]').value = result.id;
        form.querySelector('[data-qc-request-url]').value = result.url;
        status.textContent = `Aanvraagnummer ${result.id}, bevestiging per e-mail.`;
        try { sessionStorage.setItem('tdp-quote-request', result.id); } catch { /* Optional. */ }
        ready = true;
        button.disabled = false;
        form.requestSubmit(button);
      } catch (error) {
        status.textContent = error.message || 'Bestandsoverdracht mislukt. Probeer opnieuw.';
        button.textContent = 'Opnieuw proberen';
      } finally {
        busy = false;
        button.disabled = false;
      }
    });
  }

  fillQuoteForm(quote, reason) {
    const metrics = this.modelMetrics();
    const props = this.lineProperties(quote);
    const values = {
      ...props,
      Volume: metrics ? `${formatNumber(metrics.volumeCm3, 2)} cm³` : '',
      Indicatieprijs: `${this.money(quote.totalIncl + this.expertcheckPriceCents(quote) / 100)} incl. btw`,
      Expertcheck: this.addons?.expertcheckState(this.addonsUi?.getState(), this.expertcheckContext(quote)).free
        ? this.addons.EXPERTCHECK_FREE_PROPERTY.value
        : this.expertcheckPriceCents(quote) > 0 ? 'Betaald €9,95' : 'Niet gekozen',
      Reden: reason,
    };
    for (const field of this.els.quoteForm.querySelectorAll('[data-qc-quote-field]')) {
      field.value = values[field.dataset.qcQuoteField] ?? '';
    }
  }

  async checkCartForOrderFee() {
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`cart.js ${response.status}`);
      const cart = await response.json();
      const items = cart.items || [];
      // Alleen een echte orderkostenregel telt; cart-order-fee.js zorgt dat die er is zolang er maatwerk staat.
      const hasOrderFee = items.some((item) => this.cartModule.isOrderFeeLineItem(item));
      // Maatwerk dat al in de winkelwagen staat telt mee voor de offertedrempel en de printuren (per bestelling).
      const existing = cartQuoteTotals(items, this.cartModule);
      const changed = hasOrderFee !== this.state.orderFeeInCart
        || existing.totalCents !== this.state.cartQuoteTotalCents
        || existing.printHours !== this.state.cartPrintHours
        || existing.rushSurchargeCents !== this.state.cartRushSurchargeCents;
      if (changed) {
        this.state.orderFeeInCart = hasOrderFee;
        this.state.cartQuoteTotalCents = existing.totalCents;
        this.state.cartPrintHours = existing.printHours;
        this.state.cartRushSurchargeCents = existing.rushSurchargeCents;
        this.updatePrice();
      }
    } catch (error) {
      if (this.addInFlight) throw error;
      console.warn('[quote-configurator] /cart.js niet bereikbaar; orderkosten worden meegerekend', error);
    }
  }

  /**
   * Toevoegen als transactie: de blokkade en prijs worden ná de laatste cart-refresh opnieuw
   * beoordeeld, het bestand gaat rechtstreeks als File in de FormData mee (geen DataTransfer),
   * en bij een onzekere response wordt eerst /cart.js op de eigen `_quote_id` gecontroleerd
   * in plaats van automatisch nog een keer te posten.
   */
  async addToCart() {
    if (this.addInFlight || this.state.parsing) return;
    const { quote, file, model } = this.state;
    if (!quote || !file || !model) return;
    if (!this.dataset.variantId) {
      this.showCtaError(this.t('error_product_missing'));
      return;
    }
    if (!this.addons || !this.addonsUi) {
      this.showCtaError('De bestandsgoedkeuring kon niet worden geladen. Vernieuw de pagina en probeer opnieuw.');
      return;
    }
    if (!this.addonsUi.validate().ok) {
      this.els.addonsStep.querySelector('[data-qc-addon-approved]')?.focus();
      return;
    }
    const generation = this.uploadGeneration;
    this.addInFlight = true;
    this.els.addToCart.disabled = true;
    this.els.addToCart.textContent = this.t('cta_adding');
    try {
      await this.checkCartForOrderFee();
      // Laatste beoordeling met de actuele cart-staat: blokkade of drempel → offerte-route, geen toevoeging.
      this.runChecks();
      this.updatePrice();
      const finalQuote = this.state.quote;
      if (generation !== this.uploadGeneration || this.state.parsing || !finalQuote || this.state.file !== file) {
        throw new CartAddError(this.t('error_cart_state'));
      }
      if (!orderFeePriceMatches(this.dataset.orderFeePriceCents, finalQuote, this.config)) throw new CartAddError('De orderkosten wijken af van het ingestelde tarief. Neem contact op; bestellen is tijdelijk geblokkeerd.');
      if (this.needsQuote(finalQuote)) {
        this.updateCta(finalQuote);
        return;
      }
      if (this.addonsUi && !this.addonsUi.validate().ok) throw new CartAddError(this.addonsUi.validate().reason);
      const transfer = await import(new URL(this.dataset.fileTransferSrc, document.baseURI).href);
      const { properties: fileProperties } = await transfer.prepareFileProperties(file, {
        variantId: this.dataset.requestVariantId, cartUrl: this.dataset.cartUrl, addUrl: this.dataset.cartAddUrl,
        updateUrl: this.dataset.cartUpdateUrl, onProgress: (text) => { this.els.addToCart.textContent = text; },
      });
      if (generation !== this.uploadGeneration || this.state.file !== file) throw new CartAddError(this.t('error_cart_state'));
      const properties = { ...this.lineProperties(finalQuote), ...fileProperties };
      const items = buildCartItems({
        main: { id: this.dataset.variantId, quantity: finalQuote.lineTotalCents, properties },
        addons: this.expertcheckAddons(finalQuote, properties),
        feeVariantId: this.dataset.orderFeeVariantId,
        orderFeeInCart: this.state.orderFeeInCart,
        addonsModule: this.addons,
        cartModule: this.cartModule,
      });
      await this.submitCart(items, properties._quote_id);
    } catch (error) {
      console.warn('[quote-configurator] Toevoegen mislukt', error);
      this.showCtaError(error instanceof CartAddError ? error.message : this.t('error_cart'));
    } finally {
      this.addInFlight = false;
      this.els.addToCart.textContent = this.t('cta_add_to_cart');
      // Knop weer bruikbaar (de foutmelding blijft staan); bij blokkade/drempel blijft hij uit.
      const current = this.state.quote;
      this.els.addToCart.disabled = this.needsQuote(current) || this.state.parsing;
    }
  }

  /**
   * Expertcheck: als aparte add-on-regel (`_addon_for` = `_quote_id`) in dezelfde call, of — als
   * hij gratis is (vanaf €150 aan maatwerk per bestelling of 25+ stuks) — alleen als property op de maatwerkregel.
   * @returns {Array<{ variantId: string, label?: string }>} add-on-regels (0 of 1)
   */
  expertcheckAddons(quote, properties) {
    const { expertcheck } = this.addonsUi?.getState() ?? {};
    if (!this.addons) return [];
    const plan = this.addons.expertcheckLinePlan({ expertcheck }, this.expertcheckContext(quote));
    if (plan.mode === 'free') properties[plan.property.name] = plan.property.value;
    return plan.mode === 'line' ? [{ variantId: this.dataset.expertcheckVariantId, label: this.state.file?.name }] : [];
  }

  /**
   * Post `items[]` als multipart naar /cart/add.js. Een 422 (te groot bestand, product niet
   * beschikbaar) wordt netjes gemeld. Bij een netwerkfout of onleesbare response wordt de
   * winkelwagen op de eigen `_quote_id` gecontroleerd: staat de regel er, dan door naar de
   * winkelwagen; anders een melding (nooit een automatische tweede post).
   */
  async submitCart(items, quoteId) {
    let response;
    try {
      response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        body: itemsToFormData(items),
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
    } catch (networkError) {
      console.warn('[quote-configurator] fetch naar /cart/add.js mislukt; winkelwagen controleren', networkError);
      if (await this.cartHasQuote(quoteId)) {
        window.location.assign(this.dataset.cartUrl);
        return;
      }
      throw new CartAddError(this.t('error_cart_network'));
    }
    if (!response.ok) {
      let detail = '';
      try {
        const body = await response.json();
        detail = body.description || body.message || '';
      } catch (parseError) {
        detail = '';
      }
      throw new CartAddError(detail ? this.t('error_cart_detail', { status: response.status, detail }) : this.t('error_cart'));
    }
    window.location.assign(this.dataset.cartUrl);
  }

  async cartHasQuote(quoteId) {
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return false;
      const cart = await response.json();
      return cartContainsQuote(cart.items, quoteId);
    } catch (error) {
      return false;
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('quote-configurator')) {
  customElements.define('quote-configurator', QuoteConfigurator);
}

export { startModelParse, startWorkerParse, configureVendorUrls, ParseCancelledError, orderFeePriceMatches, QuoteConfigurator, buildVertexColors, applyColorMap, effectiveColorCount, hexToRgb, buildCartItems, itemsToFormData, cartContainsQuote, cartQuoteTotals, parsePrintHours, initialSelection, parseMillimeters, normalizeMaterialColor };
